import { useState, useEffect, useCallback, useRef, Component } from "react";
import { Link } from "react-router-dom";
import apiFetch, { getAuthHeaders } from "../../../shared/services/apiClient";
import { setMultimonedaFlag } from "../../../shared/hooks/useMultimoneda";
import { overlayClose } from "../../../shared/utils/overlayClose";

const BACKUP_BASE_URL = process.env.REACT_APP_API_URL ?? "";

// ── Error Boundary ────────────────────────────────────────────────────────────
class AdminErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[AdminPage] Error capturado:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "40px 24px", background: "rgba(239,68,68,0.06)",
          border: "1px solid rgba(239,68,68,0.2)", borderRadius: 12,
          color: "#fca5a5", fontFamily: "'Inter',sans-serif",
        }}>
          <div style={{ fontSize: "1.5rem", marginBottom: 12 }}>⚠️ Error al cargar el panel</div>
          <pre style={{ fontSize: "0.78rem", color: "#f87171", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {this.state.error?.message}
            {"\n\n"}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: 16, padding: "8px 18px", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#f87171", cursor: "pointer", fontSize: "0.85rem" }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Design tokens ─────────────────────────────────────────────────────────────
// Antes eran hex fijos (#0f172a...) — ignoraban el tema claro/oscuro del resto
// del sistema. Ahora apuntan a las mismas variables CSS que usa toda la app,
// así este panel se ve y se comporta igual que Dashboard/CxC/CxP/Informes.
const C = {
  bg0: "var(--bg)", bg1: "var(--bg-card)", bg2: "var(--bg-card)", bg3: "var(--hover-bg)",
  border: "var(--border-card)", accent: "#f97316",
  text: "var(--text-primary)", muted: "var(--text-muted)",
};

// ── Primitives ────────────────────────────────────────────────────────────────
function Input({ label, required, error, type = "text", ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          display: "block", fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 5,
          color: error ? "#f87171" : C.muted,
        }}>
          {label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}
        </label>
      )}
      <input type={type} {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
          width: "100%", background: "var(--bg-input, var(--bg))", borderRadius: 9,
          border: `1px solid ${error ? "#f87171" : focus ? C.accent : "var(--border-input, var(--border-card))"}`,
          padding: "10px 14px", color: C.text, fontSize: "0.88rem",
          outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: focus ? "0 0 0 3px rgba(249,115,22,0.12)" : "none",
        }} />
      {error && <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Select({ label, required, error, children, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          display: "block", fontSize: "0.7rem", fontWeight: 700,
          letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 5,
          color: error ? "#f87171" : C.muted,
        }}>
          {label}{required && <span style={{ color: "#f87171", marginLeft: 3 }}>*</span>}
        </label>
      )}
      <select {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
        width: "100%", background: "var(--bg-input, var(--bg))", borderRadius: 9,
        border: `1px solid ${error ? "#f87171" : focus ? C.accent : "var(--border-input, var(--border-card))"}`,
        padding: "10px 14px", color: C.text, fontSize: "0.88rem",
        outline: "none", boxSizing: "border-box", transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focus ? "0 0 0 3px rgba(249,115,22,0.12)" : "none",
      }}>
        {children}
      </select>
      {error && <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, small, type = "button" }) {
  const [h, setH] = useState(false);
  const cfg = {
    primary: { bg: h ? "#ea6b10" : "#f97316", color: "#fff", border: "#f97316" },
    ghost:   { bg: h ? "var(--hover-bg)" : "transparent", color: C.muted, border: C.border },
    danger:  { bg: h ? "#dc2626" : "#ef4444", color: "#fff", border: "#ef4444" },
    success: { bg: h ? "#059669" : "#10b981", color: "#fff", border: "#10b981" },
  }[variant] ?? {};
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 9,
        color: cfg.color, padding: small ? "6px 13px" : "10px 20px",
        fontSize: small ? "0.78rem" : "0.86rem", fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        transition: "all 0.15s", display: "inline-flex", alignItems: "center",
        gap: 8, whiteSpace: "nowrap",
      }}>
      {children}
    </button>
  );
}

function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  const ok = type !== "error";
  const color = ok ? "#4ade80" : "#f87171";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
      border: `1px solid ${ok ? "rgba(74,222,128,0.28)" : "rgba(239,68,68,0.28)"}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 9, padding: "11px 16px", marginBottom: 16,
      color, fontSize: "0.84rem", fontWeight: 500,
    }}>
      <i className={`fa-solid ${ok ? "fa-circle-check" : "fa-circle-exclamation"}`} />
      <span style={{ flex: 1, color: "var(--text-primary)" }}>{msg}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "0.9rem", padding: 0 }}>
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

// Banner de error persistente — usado cuando una carga falla, para que el motivo
// real (403/500/sin sesión) sea visible en vez de mostrar una pestaña vacía.
function ErrorBanner({ msg, onRetry }) {
  if (!msg) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.28)",
      borderLeft: "3px solid #ef4444", borderRadius: 9, padding: "12px 16px",
      marginBottom: 16, color: "#f87171", fontSize: "0.84rem",
    }}>
      <i className="fa-solid fa-circle-exclamation" />
      <span style={{ flex: 1, color: "var(--text-primary)" }}>{msg}</span>
      {onRetry && (
        <button onClick={onRetry} style={{
          background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 7, color: "#f87171", cursor: "pointer", fontSize: "0.78rem",
          padding: "5px 13px", fontWeight: 700,
        }}>
          <i className="fa-solid fa-rotate-right" style={{ marginRight: 5 }} />Reintentar
        </button>
      )}
    </div>
  );
}

// Lee la respuesta como JSON, lanzando un Error descriptivo si el status no es OK.
async function readJsonOrThrow(res, label) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.error ||
      (res.status === 403 ? "No tenés permisos de superadministrador." : `Error ${res.status}`);
    throw new Error(`${label}: ${detail}`);
  }
  return res.json();
}

function Modal({ title, icon, onClose, children, wide }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.66)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} {...overlayClose(onClose)}>
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: 16, width: wide ? "min(680px,97vw)" : "min(540px,95vw)",
        maxHeight: "90vh", overflow: "auto", padding: 26,
        boxShadow: "0 28px 70px rgba(0,0,0,0.45)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12 }}>
          <h3 style={{ margin: 0, color: C.text, fontSize: "1.06rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            {icon && (
              <span style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(249,115,22,0.15)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className={`fa-solid ${icon}`} style={{ color: "#f97316", fontSize: "0.88rem" }} />
              </span>
            )}
            {title}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: "1.05rem", flexShrink: 0 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Multimoneda Toggle ────────────────────────────────────────────────────────
function MultimonedaToggle({ c, onToggle }) {
  const active = !!c.multimoneda_activa;
  const [h, setH] = useState(false);
  return (
    <button type="button"
      title={active ? "Desactivar multimoneda" : "Activar multimoneda (USD)"}
      onClick={() => onToggle(!active)}
      onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        background: active
          ? h ? "rgba(96,165,250,0.25)" : "rgba(96,165,250,0.14)"
          : h ? "var(--hover-bg)" : "transparent",
        border: `1px solid ${active ? "rgba(96,165,250,0.4)" : C.border}`,
        borderRadius: 20, padding: "4px 11px",
        color: active ? "#60a5fa" : C.muted,
        fontSize: "0.73rem", fontWeight: 700,
        cursor: "pointer", transition: "all 0.15s",
        display: "inline-flex", alignItems: "center", gap: 5,
      }}>
      <i className="fa-solid fa-dollar-sign" />
      {active ? "USD ON" : "USD OFF"}
    </button>
  );
}

// ── Demo comercial Tab ────────────────────────────────────────────────────────
function DemoTab() {
  const [estado,   setEstado]   = useState(null); // null = cargando
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState("");
  const [creating, setCreating] = useState(false);
  const [toast,    setToast]    = useState({ msg: "", type: "" });
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetText, setResetText] = useState("");
  const [credenciales, setCredenciales] = useState(null); // { usuario, password } tras crear/recrear
  const [copiado, setCopiado] = useState("");

  function showToast(msg, ok = true) {
    setToast({ msg, type: ok ? "" : "error" });
    setTimeout(() => setToast({ msg: "", type: "" }), 4000);
  }

  const cargar = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const res  = await apiFetch("/api/core/admin/demo/");
      const data = await readJsonOrThrow(res, "Estado del demo");
      setEstado(data);
    } catch (e) {
      setLoadErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function crear(reset) {
    setCreating(true);
    try {
      const res  = await apiFetch("/api/core/admin/demo/", {
        method: "POST",
        body: JSON.stringify(reset ? { reset: true } : {}),
      });
      const data = await readJsonOrThrow(res, reset ? "Recrear demo" : "Crear demo");
      setCredenciales({ usuario: data.usuario, password: data.password });
      if (data.avisos?.length) {
        showToast(`Demo ${reset ? "recreado" : "creado"} con ${data.avisos.length} aviso(s) - revisá los logs del servidor.`);
      } else {
        showToast(`Demo ${reset ? "recreado" : "creado"} correctamente ✓`);
      }
      setConfirmReset(false);
      setResetText("");
      await cargar();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setCreating(false);
    }
  }

  function copiar(texto, campo) {
    navigator.clipboard?.writeText(texto).then(() => {
      setCopiado(campo);
      setTimeout(() => setCopiado(""), 1500);
    });
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />Cargando...
    </div>;
  }

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={cargar} />

      <p style={{ color: C.muted, fontSize: "0.85rem", margin: "0 0 18px", lineHeight: 1.6 }}>
        Condominio de prueba con datos ficticios realistas (facturas, recibos, proveedores, varios
        meses de historial) para mostrarle el sistema a un prospecto sin tocar datos reales.
        Compartile el usuario y contraseña de acceso.
      </p>

      {!estado?.existe && (
        <div style={{
          background: C.bg1, border: `1px dashed ${C.border}`, borderRadius: 12,
          padding: 28, textAlign: "center",
        }}>
          <i className="fa-solid fa-eye" style={{ fontSize: "1.6rem", color: C.accent, marginBottom: 10, display: "block" }} />
          <p style={{ color: C.muted, fontSize: "0.86rem", margin: "0 0 16px" }}>
            Todavía no existe un condominio demo.
          </p>
          <Btn onClick={() => crear(false)} disabled={creating}>
            {creating
              ? <><i className="fa-solid fa-circle-notch fa-spin" />Creando... (puede tardar un minuto)</>
              : <><i className="fa-solid fa-wand-magic-sparkles" />Crear demo</>}
          </Btn>
        </div>
      )}

      {estado?.existe && (
        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text, fontSize: "0.95rem", marginBottom: 4 }}>
                <i className="fa-solid fa-building" style={{ color: C.accent, marginRight: 8 }} />
                {estado.nombre}
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: "0.78rem", color: C.muted }}>
                <span>{estado.facturas_cxc} facturas CxC</span>
                <span>{estado.recibos_cxc} recibos CxC</span>
                <span>{estado.facturas_cxp} facturas CxP</span>
              </div>
            </div>
            <Btn variant="ghost" small onClick={() => setConfirmReset(true)} disabled={creating}>
              <i className="fa-solid fa-rotate-right" />Recrear demo
            </Btn>
          </div>

          <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
              Acceso para el prospecto
            </div>
            <CredencialRow label="Usuario" valor={estado.usuario} onCopy={() => copiar(estado.usuario, "usuario")} copiado={copiado === "usuario"} />
            {credenciales?.password && (
              <CredencialRow label="Contraseña" valor={credenciales.password} onCopy={() => copiar(credenciales.password, "password")} copiado={copiado === "password"} />
            )}
            {!credenciales?.password && (
              <p style={{ color: C.muted, fontSize: "0.76rem", margin: "6px 0 0" }}>
                La contraseña solo se muestra al crear o recrear el demo (no se guarda en pantalla por seguridad).
                Si la perdiste, usá "Recrear demo".
              </p>
            )}
          </div>
        </div>
      )}

      {confirmReset && (
        <Modal title="Recrear el condominio demo" icon="fa-triangle-exclamation" onClose={() => { setConfirmReset(false); setResetText(""); }}>
          <p style={{ color: C.text, fontSize: "0.88rem", marginBottom: 10 }}>
            Esto borra <strong>todos</strong> los datos actuales del demo (facturas, recibos, catálogo)
            y los vuelve a sembrar de cero. Si el prospecto está mirando el demo en este momento, va a
            perder lo que tenía cargado.
          </p>
          <p style={{ color: C.muted, fontSize: "0.8rem", marginBottom: 14 }}>
            Escribí <strong>RECREAR</strong> para confirmar:
          </p>
          <Input value={resetText} onChange={e => setResetText(e.target.value)} placeholder="RECREAR" />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => { setConfirmReset(false); setResetText(""); }}>Cancelar</Btn>
            <Btn variant="danger" disabled={resetText !== "RECREAR" || creating} onClick={() => crear(true)}>
              {creating
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Recreando...</>
                : <><i className="fa-solid fa-rotate-right" />Recrear demo</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CredencialRow({ label, valor, onCopy, copiado }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <span style={{ fontSize: "0.76rem", color: C.muted, minWidth: 78 }}>{label}</span>
      <code style={{
        background: "var(--bg-input, var(--bg))", border: `1px solid ${C.border}`, borderRadius: 7,
        padding: "6px 12px", fontSize: "0.85rem", color: C.text, flex: 1, minWidth: 0,
      }}>
        {valor}
      </code>
      <button onClick={onCopy} title="Copiar" style={{
        background: "none", border: `1px solid ${C.border}`, borderRadius: 7,
        padding: "6px 10px", cursor: "pointer", color: copiado ? "#4ade80" : C.muted,
      }}>
        <i className={`fa-solid ${copiado ? "fa-check" : "fa-copy"}`} />
      </button>
    </div>
  );
}

// ── Condominios Tab ───────────────────────────────────────────────────────────
function CondominiosTab() {
  const [condominios, setCond] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [modal, setModal]      = useState(null);
  const [form, setForm]        = useState({});
  const [errors, setErrors]    = useState({});
  const [saving, setSaving]    = useState(false);
  const [toast, setToast]      = useState({ msg: "", type: "" });
  const [deleting, setDeleting] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delText, setDelText]   = useState("");
  const [confirmReset, setConfirmReset] = useState(null);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [loadErr, setLoadErr]   = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    apiFetch("/api/core/admin/condominios/")
      .then(r => readJsonOrThrow(r, "Condominios"))
      .then(d => setCond(Array.isArray(d) ? d : []))
      .catch(e => setLoadErr(e.message || "Error al cargar condominios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ cedulaJuridica: "", nombre: "", email: "", direccion: "", tipo: "condominio" });
    setErrors({});
    setModal("create");
  }

  function openEdit(c) {
    setForm({ ...c });
    setErrors({});
    setModal(c);
  }

  async function handleSave() {
    const errs = {};
    if (!form.cedulaJuridica?.trim()) errs.cedulaJuridica = "Requerido";
    if (!form.nombre?.trim())         errs.nombre         = "Requerido";
    if (!form.email?.trim())          errs.email          = "Requerido";
    if (Object.keys(errs).length)     { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/condominios/${modal.id}/` : "/api/core/admin/condominios/";
      const method = isEdit ? "PATCH" : "POST";
      const res    = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (!res.ok) {
        const e = await res.json();
        setErrors(typeof e === "object" ? e : { nombre: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Condominio actualizado ✓" : "Condominio creado ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    if (delText !== c.nombre) return;
    setDeleting(c.id);
    setConfirmDel(null);
    try {
      // El backend exige el nombre exacto (mismo criterio que el reset total):
      // borra todos los datos del condominio y luego el registro.
      const res = await apiFetch(`/api/core/admin/condominios/${c.id}/`, {
        method: "DELETE",
        body: JSON.stringify({ confirmacion: c.nombre }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.detail || "Error al eliminar condominio", "error");
        return;
      }
      load();
      if (data.fallos && data.fallos.length) {
        showToast(`"${c.nombre}" eliminado con ${data.fallos.length} advertencia(s)`, "error");
      } else {
        showToast(`"${c.nombre}" eliminado`);
      }
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
      setDelText("");
    }
  }

  async function handleReset() {
    if (!confirmReset || resetText !== confirmReset.nombre) return;
    setResetting(true);
    try {
      const res = await apiFetch(`/api/core/admin/condominios/${confirmReset.id}/reset/`, {
        method: "POST",
        body: JSON.stringify({ confirmacion: resetText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.detail || "Error al reiniciar el condominio", "error");
        return;
      }
      setConfirmReset(null);
      setResetText("");
      load();
      showToast(data.detail || `"${confirmReset.nombre}" reiniciado desde cero ✓`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setResetting(false);
    }
  }

  const [cargandoCatalogo, setCargandoCatalogo] = useState(null); // id del condo en curso

  async function handleCargarCatalogo(c) {
    setCargandoCatalogo(c.id);
    try {
      const res = await apiFetch(`/api/core/admin/condominios/${c.id}/catalogo/`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(data.detail || "Error al cargar el catálogo", "error"); return; }
      showToast(data.detail || `Catálogo cargado para "${c.nombre}"`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setCargandoCatalogo(null);
    }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{condominios.length} condominios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-plus" />Nuevo condominio</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {condominios.map(c => (
            <div key={c.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fa-solid fa-building" style={{ color: C.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{c.nombre}</div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 2 }}>
                  {c.cedulaJuridica} · {c.email}
                  {c.tipo === "cliente" && (
                    <span style={{
                      marginLeft: 8, background: "rgba(249,115,22,0.15)",
                      border: "1px solid rgba(249,115,22,0.3)", borderRadius: 4,
                      padding: "1px 7px", color: "#f97316", fontSize: "0.7rem", fontWeight: 600,
                    }}>Cliente</span>
                  )}
                  {c.multimoneda_activa && (
                    <span style={{
                      marginLeft: 8, background: "rgba(96,165,250,0.15)",
                      border: "1px solid rgba(96,165,250,0.3)", borderRadius: 4,
                      padding: "1px 7px", color: "#60a5fa", fontSize: "0.7rem", fontWeight: 600,
                    }}>Multimoneda ✓</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <MultimonedaToggle c={c} onToggle={async (newVal) => {
                  if (newVal && !window.confirm(
                    `¿Activar multimoneda en "${c.nombre}"?\n\nDeberá realizar un asiento de ajuste manual para registrar los saldos en dólares existentes.`
                  )) return;
                  if (!newVal && !window.confirm(
                    `¿Desactivar multimoneda en "${c.nombre}"?\n\nLas columnas USD se ocultarán.`
                  )) return;
                  try {
                    const res = await apiFetch(`/api/core/admin/condominios/${c.id}/`, {
                      method: "PATCH",
                      body: JSON.stringify({ multimoneda_activa: newVal }),
                    });
                    if (!res.ok) { showToast("Error al cambiar multimoneda", "error"); return; }
                    // Si el condominio que se editó es el activo del usuario, sincronizar el flag
                    const condominioId = localStorage.getItem("condominioId");
                    if (condominioId && String(c.id) === String(condominioId)) {
                      setMultimonedaFlag(newVal);
                    }
                    load();
                    showToast(`Multimoneda ${newVal ? "activada" : "desactivada"} para "${c.nombre}"`);
                  } catch (e) { showToast(e.message, "error"); }
                }} />
                <Btn small variant="ghost" onClick={() => openEdit(c)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="ghost" disabled={cargandoCatalogo === c.id}
                  title="Cargar/completar el catálogo de cuentas base (no borra nada)"
                  onClick={() => handleCargarCatalogo(c)}>
                  {cargandoCatalogo === c.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-sitemap" style={{ color: "#60a5fa" }} />}
                </Btn>
                <Btn small variant="ghost" title="Reiniciar desde cero (borra todos los datos)"
                  onClick={() => { setConfirmReset(c); setResetText(""); }}>
                  <i className="fa-solid fa-rotate-left" style={{ color: "#fb923c" }} />
                </Btn>
                <Btn small variant="danger" disabled={deleting === c.id}
                  onClick={() => setConfirmDel(c)}>
                  {deleting === c.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {condominios.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className="fa-solid fa-building" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay condominios registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? "Nuevo Condominio" : "Editar Condominio"} onClose={() => setModal(null)}>
          <Select label="Tipo de organización" {...f("tipo")} error={errors.tipo}>
            <option value="condominio">Condominio (residentes)</option>
            <option value="cliente">Cliente (facturación a clientes)</option>
          </Select>
          <Input label="Nombre" required {...f("nombre")} error={errors.nombre} placeholder="Residencial Los Pinos" />
          <Input label="Cédula Jurídica" required {...f("cedulaJuridica")} error={errors.cedulaJuridica} placeholder="3-102-000000" />
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="admin@condominio.com" />
          <Input label="Dirección" {...f("direccion")} placeholder="San José, Costa Rica" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Confirmar eliminar condominio */}
      {confirmDel && (
        <Modal title="Eliminar condominio" onClose={() => { setConfirmDel(null); setDelText(""); }}>
          <div style={{ padding: "8px 0 14px" }}>
            <div style={{ textAlign: "center", fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700, textAlign: "center" }}>
              ¿Eliminar "{confirmDel.nombre}"?
            </p>
            <p style={{ color: "#f87171", fontSize: "0.83rem", lineHeight: 1.6, margin: "0 0 14px" }}>
              Esta acción es <strong>permanente e irreversible</strong>. Se eliminarán todos los
              datos del condominio (filiales, residentes, facturas, recibos, asientos, cuentas
              bancarias, préstamos, proveedores y catálogo de cuentas) y luego el condominio.
            </p>
            <p style={{ color: C.muted, fontSize: "0.8rem", margin: "0 0 6px" }}>
              Para confirmar, escribí el nombre exacto del condominio:
            </p>
            <input
              value={delText}
              onChange={e => setDelText(e.target.value)}
              placeholder={confirmDel.nombre}
              style={{
                width: "100%", padding: "9px 12px", borderRadius: 7,
                background: C.bg1, border: `1px solid ${delText === confirmDel.nombre ? "rgba(74,222,128,0.5)" : C.border}`,
                color: C.text, fontSize: "0.88rem", outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => { setConfirmDel(null); setDelText(""); }}>Cancelar</Btn>
            <Btn variant="danger" disabled={delText !== confirmDel.nombre} onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar permanentemente
            </Btn>
          </div>
        </Modal>
      )}

      {/* Confirmar reset total del condominio */}
      {confirmReset && (
        <Modal title="Reiniciar condominio desde cero" onClose={() => { setConfirmReset(null); setResetText(""); }}>
          <div style={{ padding: "4px 0 14px" }}>
            <div style={{ textAlign: "center", fontSize: "2.2rem", marginBottom: 10 }}>⚠️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700, textAlign: "center" }}>
              ¿Reiniciar "{confirmReset.nombre}" en su totalidad?
            </p>
            <p style={{ color: "#f87171", fontSize: "0.83rem", lineHeight: 1.6, margin: "0 0 14px" }}>
              Se borrarán <strong>TODOS los datos</strong>: filiales, residentes, facturas, recibos,
              asientos, cuentas bancarias, proveedores, catálogo de cuentas, áreas comunes y avisos.
              Los consecutivos vuelven a 0 y el catálogo base se regenera.
              Los <strong>usuarios NO se eliminan</strong>. Esta acción es <strong>irreversible</strong>.
            </p>
            <p style={{ color: C.muted, fontSize: "0.8rem", margin: "0 0 6px" }}>
              Para confirmar, escribí el nombre exacto del condominio:
            </p>
            <input
              value={resetText}
              onChange={e => setResetText(e.target.value)}
              placeholder={confirmReset.nombre}
              style={{
                width: "100%", padding: "9px 12px", borderRadius: 7,
                background: C.bg1, border: `1px solid ${resetText === confirmReset.nombre ? "rgba(74,222,128,0.5)" : C.border}`,
                color: C.text, fontSize: "0.88rem", outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => { setConfirmReset(null); setResetText(""); }}>Cancelar</Btn>
            <Btn variant="danger" disabled={resetText !== confirmReset.nombre || resetting} onClick={handleReset}>
              {resetting
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Reiniciando…</>
                : <><i className="fa-solid fa-rotate-left" />Sí, reiniciar desde cero</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Usuarios Tab ──────────────────────────────────────────────────────────────
// ── Permisos por módulo ───────────────────────────────────────────────────────
// Checklist agrupado igual que el menú. `modulos` viene de
// /api/core/admin/permisos-modulos/ ({clave, label, grupo, roles, defecto}).
function permisosPorDefecto(modulos, rol) {
  return modulos.filter(m => (m.defecto || []).includes(rol)).map(m => m.clave);
}

function PermisosPicker({ modulos, rol, value, onChange, titulo = "Módulos que puede ver" }) {
  const aplicables = modulos.filter(m => (m.roles || []).includes(rol));
  const grupos = [];
  for (const m of aplicables) {
    let g = grupos.find(x => x.nombre === m.grupo);
    if (!g) { g = { nombre: m.grupo, items: [] }; grupos.push(g); }
    g.items.push(m);
  }
  const marcados = new Set(value || []);

  function toggle(clave) {
    const next = new Set(marcados);
    next.has(clave) ? next.delete(clave) : next.add(clave);
    onChange([...next]);
  }
  function toggleGrupo(items, todos) {
    const next = new Set(marcados);
    items.forEach(m => (todos ? next.delete(m.clave) : next.add(m.clave)));
    onChange([...next]);
  }

  if (aplicables.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={{ fontSize: "0.8rem", color: C.muted, fontWeight: 600 }}>
          {titulo} ({marcados.size} de {aplicables.length})
        </label>
        <div style={{ display: "flex", gap: 10, fontSize: "0.74rem" }}>
          <button type="button" onClick={() => onChange(aplicables.map(m => m.clave))}
            style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", padding: 0 }}>Todos</button>
          <button type="button" onClick={() => onChange([])}
            style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 0 }}>Ninguno</button>
        </div>
      </div>
      <div style={{
        maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10,
        padding: "10px 12px", background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
      }}>
        {grupos.map(g => {
          const todos = g.items.every(m => marcados.has(m.clave));
          const algunos = g.items.some(m => marcados.has(m.clave));
          return (
            <div key={g.nombre}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, color: C.text, marginBottom: 4 }}>
                <input type="checkbox" checked={todos}
                  ref={el => { if (el) el.indeterminate = algunos && !todos; }}
                  onChange={() => toggleGrupo(g.items, todos)} />
                {g.nombre}
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "3px 12px", paddingLeft: 22 }}>
                {g.items.map(m => (
                  <label key={m.clave} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.78rem", color: C.muted }}>
                    <input type="checkbox" checked={marcados.has(m.clave)} onChange={() => toggle(m.clave)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ color: C.muted, fontSize: "0.72rem", margin: "6px 0 0" }}>
        Lo que no esté marcado desaparece del menú.
        {rol !== "AGUA" && rol !== "SEGURIDAD" && " Los datos compartidos (filiales, facturas, cuentas) se pueden seguir leyendo desde otras pantallas, pero no crear, editar ni borrar."}
        {titulo === "Permisos generales" && " Valen en todos sus condominios, salvo en los que tengan permisos propios (botón Condominios)."}
      </p>
    </div>
  );
}

// ── Condominios asignados + permisos por condominio ───────────────────────────
// Sirve a usuarios, contadores y operadores de agua. En cada condominio el
// usuario usa sus permisos generales, o unos propios si se marca "Personalizar".
// El condominio principal de un usuario no se puede quitar desde acá (se cambia
// en Editar).
function AccesosModal({ usuario, endpoint, rol, ayuda, colorBg, colorBorde, condominios, modulos, onClose, onSaved, showToast }) {
  const [accesos, setAccesos] = useState(() => {
    const m = new Map();
    (usuario.condominios || []).forEach(c => m.set(c.id, c.permisos ?? null));
    return m;
  });
  const [abierto, setAbierto] = useState(null);
  const [saving, setSaving]   = useState(false);
  const principalId = (usuario.condominios || []).find(c => c.principal)?.id ?? null;
  const generales   = usuario.permisos || [];
  const aplicables  = modulos.filter(m => (m.roles || []).includes(rol)).length;

  function toggleCondo(id) {
    if (id === principalId) return;
    setAccesos(prev => {
      const next = new Map(prev);
      next.has(id) ? next.delete(id) : next.set(id, null);
      return next;
    });
  }
  function setPermisos(id, permisos) {
    setAccesos(prev => new Map(prev).set(id, permisos));
  }

  async function guardar() {
    setSaving(true);
    try {
      const body = { accesos: [...accesos].map(([condominio_id, permisos]) => ({ condominio_id, permisos })) };
      const res = await apiFetch(`/api/core/admin/${endpoint}/${usuario.id}/condominios/`, {
        method: "PUT", body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || e.condominio_ids || e.accesos || "Error al guardar", "error");
        return;
      }
      onSaved();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  // Principal primero, después los asignados, después el resto.
  const orden = [...condominios].sort((a, b) =>
    (b.id === principalId) - (a.id === principalId) || accesos.has(b.id) - accesos.has(a.id)
    || a.nombre.localeCompare(b.nombre));

  return (
    <Modal title={`Condominios - ${usuario.username}`} onClose={onClose} wide>
      <p style={{ color: C.muted, fontSize: "0.84rem", marginTop: 0 }}>{ayuda}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "55vh", overflowY: "auto", marginBottom: 16 }}>
        {condominios.length === 0 && (
          <div style={{ color: C.muted, textAlign: "center", padding: 20 }}>No hay condominios disponibles.</div>
        )}
        {orden.map(c => {
          const asignado = accesos.has(c.id);
          const propios  = accesos.get(c.id);
          const personal = asignado && Array.isArray(propios);
          return (
            <div key={c.id} style={{
              background: asignado ? colorBg : C.bg1,
              border: `1px solid ${asignado ? colorBorde : C.border}`,
              borderRadius: 8, padding: "10px 14px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: c.id === principalId ? "default" : "pointer", flex: 1, minWidth: 180 }}>
                  <input type="checkbox" checked={asignado} disabled={c.id === principalId}
                    onChange={() => toggleCondo(c.id)} />
                  <span style={{ fontWeight: 600, color: C.text, fontSize: "0.88rem" }}>{c.nombre}</span>
                  {c.id === principalId && (
                    <span style={{ fontSize: "0.66rem", fontWeight: 700, padding: "1px 7px", borderRadius: 4,
                      background: "rgba(96,165,250,0.12)", color: "#60a5fa" }}>PRINCIPAL</span>
                  )}
                </label>
                {asignado && aplicables > 0 && (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {[["gen", "Permisos generales"], ["per", "Personalizar"]].map(([k, label]) => {
                      const activo = (k === "per") === personal;
                      return (
                        <button key={k} type="button"
                          onClick={() => {
                            if (k === "gen") { setPermisos(c.id, null); if (abierto === c.id) setAbierto(null); }
                            else { if (!personal) setPermisos(c.id, [...generales]); setAbierto(c.id); }
                          }}
                          style={{ fontSize: "0.72rem", fontWeight: 700, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
                            border: `1px solid ${activo ? C.accent : C.border}`,
                            background: activo ? "rgba(249,115,22,0.12)" : "transparent",
                            color: activo ? C.accent : C.muted }}>
                          {label}
                        </button>
                      );
                    })}
                    {personal && (
                      <button type="button" onClick={() => setAbierto(abierto === c.id ? null : c.id)}
                        title={abierto === c.id ? "Ocultar" : "Ver permisos"}
                        style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: "4px 6px" }}>
                        <i className={`fa-solid fa-chevron-${abierto === c.id ? "up" : "down"}`} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {personal && abierto !== c.id && (
                <div style={{ fontSize: "0.74rem", color: C.muted, marginTop: 6, paddingLeft: 28 }}>
                  {propios.length} de {aplicables} módulos en este condominio
                </div>
              )}
              {personal && abierto === c.id && (
                <div style={{ marginTop: 10 }}>
                  <PermisosPicker modulos={modulos} rol={rol} value={propios}
                    onChange={p => setPermisos(c.id, p)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: C.muted, fontSize: "0.82rem" }}>{accesos.size} asignado(s)</span>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={guardar} disabled={saving}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando...</>
              : <><i className="fa-solid fa-floppy-disk" />Guardar accesos</>}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function UsuariosTab() {
  const [usuarios, setUsuarios]   = useState([]);
  const [condominios, setCond]    = useState([]);
  const [modulos, setModulos]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState({});
  const [errors, setErrors]       = useState({});
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState({ msg: "", type: "" });
  const [showPw, setShowPw]       = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [loadErr, setLoadErr]     = useState("");
  const [assignModal, setAssignModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      apiFetch("/api/core/admin/usuarios/").then(r => readJsonOrThrow(r, "Usuarios")),
      apiFetch("/api/core/admin/condominios/").then(r => readJsonOrThrow(r, "Condominios")),
      apiFetch("/api/core/admin/permisos-modulos/").then(r => readJsonOrThrow(r, "Módulos")),
    ]).then(([u, c, m]) => {
      setUsuarios(Array.isArray(u) ? u : []);
      setCond(Array.isArray(c) ? c : []);
      setModulos(Array.isArray(m) ? m : []);
    }).catch(e => setLoadErr(e.message || "Error al cargar usuarios"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ username: "", password: "", email: "", first_name: "", last_name: "", condominio_id: "", is_superuser: false,
      permisos: permisosPorDefecto(modulos, "USUARIO") });
    setErrors({});
    setModal("create");
    setShowPw(false);
  }

  function openEdit(u) {
    setForm({ ...u, password: "", condominio_id: u.condominio_id ?? "", permisos: u.permisos || [] });
    setErrors({});
    setModal(u);
    setShowPw(false);
  }

  async function handleSave() {
    const errs = {};
    if (!form.username?.trim()) errs.username = "Requerido";
    if (modal === "create" && !form.password?.trim()) errs.password = "Requerido";
    if (modal === "create" && form.password && form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (!form.is_superuser && !form.condominio_id) errs.condominio_id = "Requerido para usuarios normales";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/usuarios/${modal.id}/` : "/api/core/admin/usuarios/";
      const method = isEdit ? "PATCH" : "POST";
      const body   = { ...form };
      if (isEdit && !body.password) delete body.password;
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setErrors(typeof e === "object" ? e : { general: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Usuario actualizado ✓" : "Usuario creado ✓");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(u) {
    setDeleting(u.id);
    setConfirmDel(null);
    try {
      const res = await apiFetch(`/api/core/admin/usuarios/${u.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.detail || "Error al eliminar usuario", "error");
        return;
      }
      load();
      showToast(`Usuario "${u.username}" eliminado`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggleActive(u) {
    try {
      const res = await apiFetch(`/api/core/admin/usuarios/${u.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (!res.ok) { showToast("Error al cambiar estado", "error"); return; }
      load();
      showToast(u.is_active ? `"${u.username}" desactivado` : `"${u.username}" activado`);
    } catch (e) { showToast(e.message, "error"); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{usuarios.length} usuarios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-user-plus" />Nuevo usuario</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {usuarios.map(u => (
            <div key={u.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
              opacity: u.is_active ? 1 : 0.6,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                background: u.is_superuser ? "rgba(96,165,250,0.15)" : "rgba(249,115,22,0.1)",
                border: `1px solid ${u.is_superuser ? "rgba(96,165,250,0.3)" : "rgba(249,115,22,0.2)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className={`fa-solid fa-${u.is_superuser ? "shield-halved" : "user"}`}
                  style={{ color: u.is_superuser ? "#60a5fa" : C.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{u.username}</span>
                  {(u.first_name || u.last_name) && (
                    <span style={{ color: C.muted, fontSize: "0.8rem" }}>
                      ({`${u.first_name || ""} ${u.last_name || ""}`.trim()})
                    </span>
                  )}
                  {u.is_superuser && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontWeight: 700 }}>ADMIN</span>
                  )}
                  {!u.is_active && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#f87171", fontWeight: 700 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 2 }}>
                  {u.email || "Sin email"}
                  {u.condominio_nombre && <> · <span style={{ color: "#94a3b8" }}>{u.condominio_nombre}</span></>}
                  {(u.condominios || []).length > 1 && (
                    <span style={{ color: "#94a3b8" }}> + {u.condominios.length - 1} más</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Btn small variant="ghost" onClick={() => handleToggleActive(u)}>
                  <i className={`fa-solid fa-${u.is_active ? "toggle-on" : "toggle-off"}`} />
                  {u.is_active ? "Activo" : "Inactivo"}
                </Btn>
                {!u.is_superuser && u.condominio_id && (
                  <Btn small variant="success" onClick={() => setAssignModal(u)}>
                    <i className="fa-solid fa-building" />Condominios
                  </Btn>
                )}
                <Btn small variant="ghost" onClick={() => openEdit(u)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="danger" disabled={deleting === u.id} onClick={() => setConfirmDel(u)}>
                  {deleting === u.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {usuarios.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className="fa-solid fa-users" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay usuarios registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? "Nuevo Usuario" : `Editar - ${modal.username}`} onClose={() => setModal(null)}>
          {errors.general && (
            <div style={{ color: "#f87171", marginBottom: 12, fontSize: "0.84rem", padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>
              {errors.general}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombre" {...f("first_name")} placeholder="María" />
            <Input label="Apellidos" {...f("last_name")} placeholder="González" />
          </div>
          <Input label="Username" required {...f("username")} error={errors.username}
            placeholder="usuario123" disabled={modal !== "create"} />
          <Input
            label={modal === "create" ? "Contraseña" : "Nueva contraseña (vacío = no cambia)"}
            required={modal === "create"} type={showPw ? "text" : "password"}
            {...f("password")} error={errors.password}
            placeholder={modal === "create" ? "Mínimo 8 caracteres" : "Dejar vacío para no cambiar"} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} />
              Mostrar contraseña
            </label>
          </div>
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="usuario@email.com" />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox"
                checked={!!form.is_superuser}
                onChange={e => setForm(p => ({ ...p, is_superuser: e.target.checked, condominio_id: e.target.checked ? "" : p.condominio_id }))} />
              <span style={{ fontWeight: 600, color: form.is_superuser ? "#60a5fa" : C.muted }}>
                Superadministrador (acceso a todos los condominios)
              </span>
            </label>
          </div>
          {!form.is_superuser && (
            <Select label="Condominio asignado" required error={errors.condominio_id}
              value={form.condominio_id ?? ""}
              onChange={e => setForm(p => ({ ...p, condominio_id: e.target.value }))}>
              <option value="">- Seleccionar condominio -</option>
              {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          )}
          {!form.is_superuser && (
            <PermisosPicker modulos={modulos} rol="USUARIO" titulo="Permisos generales"
              value={form.permisos} onChange={permisos => setForm(p => ({ ...p, permisos }))} />
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Otros condominios del usuario y permisos por condominio */}
      {assignModal && (
        <AccesosModal usuario={assignModal} endpoint="usuarios" rol="USUARIO"
          ayuda={`Además de su condominio principal (${assignModal.condominio_nombre}), elegí en qué otros condominios puede trabajar. En cada uno usa sus permisos generales, o podés personalizarlos para ese condominio.`}
          colorBg="rgba(249,115,22,0.08)" colorBorde="rgba(249,115,22,0.3)"
          condominios={condominios} modulos={modulos} showToast={showToast}
          onClose={() => setAssignModal(null)}
          onSaved={() => { setAssignModal(null); load(); showToast("Condominios asignados ✓"); }} />
      )}

      {/* Confirmar eliminar usuario */}
      {confirmDel && (
        <Modal title="Eliminar usuario" onClose={() => setConfirmDel(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
              ¿Eliminar al usuario "{confirmDel.username}"?
            </p>
            <p style={{ color: C.muted, fontSize: "0.83rem", lineHeight: 1.6, margin: 0 }}>
              Esta acción no se puede deshacer.
              {confirmDel.condominio_nombre && ` El usuario está asociado al condominio "${confirmDel.condominio_nombre}".`}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Usuarios multi-condominio (Contadores / Operadores de Agua) ───────────────
// Los dos perfiles se administran igual: usuario sin condominio fijo + lista de
// condominios asignados. Cambian solo el endpoint y los rótulos, así que la
// pestaña se parametriza en vez de duplicarse.
const PERFIL_CONTADOR = {
  endpoint:    "contadores",
  rol:         "CONTADOR",
  titulo:      "Contadores",
  Singular:    "Contador",
  singular:    "contador",
  plural:      "contadores",
  icono:       "fa-calculator",
  color:       "#10b981",
  colorBg:     "rgba(16,185,129,0.12)",
  colorBorde:  "rgba(16,185,129,0.3)",
  badge:       "CONTADOR",
  phUsuario:   "contador123",
  phEmail:     "contador@email.com",
  ayudaAsignar: "Selecciona los condominios a los que este contador tendrá acceso:",
};

const PERFIL_AGUA = {
  endpoint:    "operadores-agua",
  rol:         "AGUA",
  titulo:      "Operadores de Agua",
  Singular:    "Operador de Agua",
  singular:    "operador de agua",
  plural:      "operadores de agua",
  icono:       "fa-droplet",
  color:       "#0284c7",
  colorBg:     "rgba(2,132,199,0.12)",
  colorBorde:  "rgba(2,132,199,0.3)",
  badge:       "AGUA",
  phUsuario:   "operador.agua",
  phEmail:     "operador@email.com",
  ayudaAsignar: "Selecciona los condominios en los que este operador podrá tomar lecturas:",
};

const PERFIL_SEGURIDAD = {
  endpoint:    "guardas",
  rol:         "SEGURIDAD",
  titulo:      "Oficiales de Seguridad",
  Singular:    "Guarda",
  singular:    "guarda",
  plural:      "guardas",
  icono:       "fa-shield-halved",
  color:       "#f97316",
  colorBg:     "rgba(249,115,22,0.12)",
  colorBorde:  "rgba(249,115,22,0.3)",
  badge:       "SEGURIDAD",
  phUsuario:   "seguridad.luis",
  phEmail:     "guarda@email.com",
  ayudaAsignar: "Selecciona los condominios en los que este guarda podrá usar la app de Seguridad:",
};

function ContadoresTab({ perfil = PERFIL_CONTADOR }) {
  const [contadores, setContadores]   = useState([]);
  const [condominios, setCond]        = useState([]);
  const [modulos, setModulos]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(null);   // null | "create" | user obj
  const [assignModal, setAssignModal] = useState(null);   // null | user obj
  const [form, setForm]               = useState({});
  const [errors, setErrors]           = useState({});
  const [saving, setSaving]           = useState(false);
  const [toast, setToast]             = useState({ msg: "", type: "" });
  const [showPw, setShowPw]           = useState(false);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [deleting, setDeleting]       = useState(null);
  const [loadErr, setLoadErr]         = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr("");
    Promise.all([
      apiFetch(`/api/core/admin/${perfil.endpoint}/`).then(r => readJsonOrThrow(r, perfil.titulo)),
      apiFetch("/api/core/admin/condominios/").then(r => readJsonOrThrow(r, "Condominios")),
      apiFetch("/api/core/admin/permisos-modulos/").then(r => readJsonOrThrow(r, "Módulos")),
    ]).then(([c, conds, m]) => {
      setContadores(Array.isArray(c) ? c : []);
      setCond(Array.isArray(conds) ? conds : []);
      setModulos(Array.isArray(m) ? m : []);
    }).catch(e => setLoadErr(e.message || `Error al cargar ${perfil.plural}`))
      .finally(() => setLoading(false));
  }, [perfil]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  function openCreate() {
    setForm({ username: "", password: "", email: "", first_name: "", last_name: "",
      permisos: permisosPorDefecto(modulos, perfil.rol) });
    setErrors({});
    setModal("create");
    setShowPw(false);
  }

  function openEdit(u) {
    setForm({ ...u, password: "", permisos: u.permisos || [] });
    setErrors({});
    setModal(u);
    setShowPw(false);
  }

  function openAssign(u) {
    setAssignModal(u);
  }

  async function handleSave() {
    const errs = {};
    if (!form.username?.trim()) errs.username = "Requerido";
    if (modal === "create" && !form.password?.trim()) errs.password = "Requerido";
    if (modal === "create" && form.password && form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit
        ? `/api/core/admin/${perfil.endpoint}/${modal.id}/`
        : `/api/core/admin/${perfil.endpoint}/`;
      const method = isEdit ? "PATCH" : "POST";
      const body   = { ...form };
      if (isEdit && !body.password) delete body.password;
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setErrors(typeof e === "object" ? e : { general: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(`${perfil.Singular} ${isEdit ? "actualizado" : "creado"} ✓`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(u) {
    setDeleting(u.id);
    setConfirmDel(null);
    try {
      const res = await apiFetch(`/api/core/admin/${perfil.endpoint}/${u.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.detail || "Error al eliminar", "error");
        return;
      }
      load();
      showToast(`${perfil.Singular} "${u.username}" eliminado`);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(null);
    }
  }

  async function handleToggleActive(u) {
    try {
      const res = await apiFetch(`/api/core/admin/${perfil.endpoint}/${u.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (!res.ok) { showToast("Error al cambiar estado", "error"); return; }
      load();
      showToast(u.is_active ? `"${u.username}" desactivado` : `"${u.username}" activado`);
    } catch (e) { showToast(e.message, "error"); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={load} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.muted, fontSize: "0.85rem" }}>{contadores.length} {perfil.plural} registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-user-plus" />Nuevo {perfil.singular}</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, marginRight: 8 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contadores.map(u => (
            <div key={u.id} style={{
              background: C.bg1, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 14,
              opacity: u.is_active ? 1 : 0.6,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                background: perfil.colorBg, border: `1px solid ${perfil.colorBorde}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className={`fa-solid ${perfil.icono}`} style={{ color: perfil.color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: "0.92rem" }}>{u.username}</span>
                  {(u.first_name || u.last_name) && (
                    <span style={{ color: C.muted, fontSize: "0.8rem" }}>
                      ({`${u.first_name || ""} ${u.last_name || ""}`.trim()})
                    </span>
                  )}
                  <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: perfil.colorBg, color: perfil.color, fontWeight: 700 }}>{perfil.badge}</span>
                  {!u.is_active && (
                    <span style={{ fontSize: "0.65rem", padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#f87171", fontWeight: 700 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ color: C.muted, fontSize: "0.77rem", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                  <span>{u.email || "Sin email"}</span>
                  {u.condominios.length > 0 ? (
                    <span style={{ color: "#94a3b8" }}>
                      {u.condominios.map(c => c.nombre + (Array.isArray(c.permisos) ? " (permisos propios)" : "")).join(" · ")}
                    </span>
                  ) : (
                    <span style={{ color: "#f87171" }}>Sin condominios asignados</span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                <Btn small variant="ghost" onClick={() => handleToggleActive(u)}>
                  <i className={`fa-solid fa-${u.is_active ? "toggle-on" : "toggle-off"}`} />
                  {u.is_active ? "Activo" : "Inactivo"}
                </Btn>
                <Btn small variant="success" onClick={() => openAssign(u)}>
                  <i className="fa-solid fa-building" />Condominios
                </Btn>
                <Btn small variant="ghost" onClick={() => openEdit(u)}>
                  <i className="fa-solid fa-pencil" />Editar
                </Btn>
                <Btn small variant="danger" disabled={deleting === u.id} onClick={() => setConfirmDel(u)}>
                  {deleting === u.id
                    ? <i className="fa-solid fa-circle-notch fa-spin" />
                    : <i className="fa-solid fa-trash" />}
                </Btn>
              </div>
            </div>
          ))}
          {contadores.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
              <i className={`fa-solid ${perfil.icono}`} style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
              No hay {perfil.plural} registrados.
            </div>
          )}
        </div>
      )}

      {/* Modal crear/editar */}
      {modal && (
        <Modal title={modal === "create" ? `Nuevo ${perfil.Singular}` : `Editar - ${modal.username}`} onClose={() => setModal(null)}>
          {errors.general && (
            <div style={{ color: "#f87171", marginBottom: 12, fontSize: "0.84rem", padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>
              {errors.general}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombre" {...f("first_name")} placeholder="Juan" />
            <Input label="Apellidos" {...f("last_name")} placeholder="Pérez" />
          </div>
          <Input label="Username" required {...f("username")} error={errors.username}
            placeholder={perfil.phUsuario} disabled={modal !== "create"} />
          <Input
            label={modal === "create" ? "Contraseña" : "Nueva contraseña (vacío = no cambia)"}
            required={modal === "create"} type={showPw ? "text" : "password"}
            {...f("password")} error={errors.password}
            placeholder={modal === "create" ? "Mínimo 8 caracteres" : "Dejar vacío para no cambiar"} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: C.muted }}>
              <input type="checkbox" checked={showPw} onChange={e => setShowPw(e.target.checked)} />
              Mostrar contraseña
            </label>
          </div>
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder={perfil.phEmail} />
          <PermisosPicker modulos={modulos} rol={perfil.rol} titulo="Permisos generales"
            value={form.permisos} onChange={permisos => setForm(p => ({ ...p, permisos }))} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Guardando…</>
                : <><i className="fa-solid fa-floppy-disk" />Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Modal asignar condominios (y permisos por condominio) */}
      {assignModal && (
        <AccesosModal usuario={assignModal} endpoint={perfil.endpoint} rol={perfil.rol}
          ayuda={perfil.ayudaAsignar} colorBg={perfil.colorBg} colorBorde={perfil.colorBorde}
          condominios={condominios} modulos={modulos} showToast={showToast}
          onClose={() => setAssignModal(null)}
          onSaved={() => { setAssignModal(null); load(); showToast("Condominios asignados ✓"); }} />
      )}

      {/* Confirmar eliminar */}
      {confirmDel && (
        <Modal title={`Eliminar ${perfil.singular}`} onClose={() => setConfirmDel(null)}>
          <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 10 }}>🗑️</div>
            <p style={{ color: C.text, margin: "0 0 8px", fontWeight: 700 }}>
              ¿Eliminar al {perfil.singular} "{confirmDel.username}"?
            </p>
            <p style={{ color: C.muted, fontSize: "0.83rem", lineHeight: 1.6, margin: 0 }}>
              Esta acción no se puede deshacer.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn variant="ghost" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => handleDelete(confirmDel)}>
              <i className="fa-solid fa-trash" />Sí, eliminar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Documentos Tab ────────────────────────────────────────────────────────────
const TIPOS_DOC = [
  // Residentes
  {
    value: "filiales", label: "Filiales", group: "Residentes",
    icon: "fa-home", color: "#a78bfa",
    cols: ["ID Filial", "Tipo Propiedad", "Núm. Plano"],
    warning: "⚠️ Eliminar filiales borrará en cascada sus propietarios, copropietarios, autorizados, facturas, recibos y CxC asociadas.",
  },
  {
    value: "propietarios", label: "Propietarios", group: "Residentes",
    icon: "fa-user-tie", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo"],
  },
  {
    value: "copropietarios", label: "Copropietarios", group: "Residentes",
    icon: "fa-user-group", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo"],
  },
  {
    value: "autorizados", label: "Autorizados", group: "Residentes",
    icon: "fa-id-card", color: "#60a5fa",
    cols: ["Filial", "Cédula", "Nombre completo", "Tipo"],
  },
  // Contabilidad — Asientos
  {
    value: "asientos", label: "Asientos de Diario", group: "Contabilidad",
    icon: "fa-book", color: "#fbbf24",
    cols: ["Número", "Fecha", "Concepto", "Tipo"],
  },
  {
    value: "ingresos", label: "Registro de Ingresos", group: "Contabilidad",
    icon: "fa-arrow-trend-up", color: "#4ade80",
    cols: ["Número", "Fecha", "Concepto"],
  },
  {
    value: "gastos", label: "Registro de Gastos", group: "Contabilidad",
    icon: "fa-receipt", color: "#f87171",
    cols: ["Número", "Fecha", "Concepto"],
  },
  {
    value: "movimientos_bancarios", label: "Movimientos Bancarios", group: "Contabilidad",
    icon: "fa-building-columns", color: "#22d3ee",
    cols: ["Documento", "Fecha", "Descripción", "Monto"],
    warning: "⚠️ Eliminar un movimiento bancario NO revierte el saldo de la cuenta bancaria ni el asiento contable relacionado - ajustalos manualmente si hace falta.",
  },
  // CxC
  {
    value: "facturas", label: "Facturas CxC", group: "Cuentas x Cobrar",
    icon: "fa-file-invoice-dollar", color: "#f97316",
    cols: ["Número", "Fecha", "Filial", "Concepto", "Monto"],
  },
  {
    value: "recibos", label: "Recibos CxC", group: "Cuentas x Cobrar",
    icon: "fa-money-check-dollar", color: "#f97316",
    cols: ["Número", "Fecha", "Filial", "Monto"],
  },
  // CxP
  {
    value: "facturas_cxp", label: "Facturas CxP", group: "Cuentas x Pagar",
    icon: "fa-file-invoice", color: "#fb923c",
    cols: ["Número", "Fecha", "Concepto", "Monto"],
  },
  {
    value: "recibos_cxp", label: "Recibos CxP", group: "Cuentas x Pagar",
    icon: "fa-money-check", color: "#fb923c",
    cols: ["Número", "Fecha", "Monto"],
  },
  {
    value: "proveedores", label: "Proveedores", group: "Cuentas x Pagar",
    icon: "fa-truck-field", color: "#fb923c",
    cols: ["Cédula", "Nombre", "Email"],
    warning: "⚠️ Eliminar un proveedor no borra sus facturas/recibos CxP existentes (la cuenta contable queda desvinculada, no se borra).",
  },
];

const GRUPOS = ["Residentes", "Contabilidad", "Cuentas x Cobrar", "Cuentas x Pagar"];

function DocumentosTab() {
  const [condominios, setCond]     = useState([]);
  const [condoId, setCondoId]      = useState("");
  const [tipo, setTipo]            = useState("filiales");
  const [search, setSearch]        = useState("");
  const [docs, setDocs]            = useState([]);
  const [total, setTotal]          = useState(0);
  const [page, setPage]            = useState(1);
  const [pages, setPages]          = useState(1);
  const [loading, setLoading]      = useState(false);
  const [selected, setSelected]    = useState(new Set());
  const [deleting, setDeleting]    = useState(false);
  const [toast, setToast]          = useState({ msg: "", type: "" });
  const [loadErr, setLoadErr]      = useState("");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 5000);
  };

  useEffect(() => {
    apiFetch("/api/core/admin/condominios/")
      .then(r => readJsonOrThrow(r, "Condominios"))
      .then(d => setCond(Array.isArray(d) ? d : []))
      .catch(e => showToast(e.message, "error"));
  }, []);

  const loadDocs = useCallback((p = 1) => {
    if (!condoId) return;
    setLoading(true);
    setLoadErr("");
    setSelected(new Set());
    apiFetch(`/api/core/admin/documentos/?condominio_id=${condoId}&tipo=${tipo}&search=${encodeURIComponent(search)}&page=${p}`)
      .then(r => readJsonOrThrow(r, "Registros"))
      .then(d => {
        setDocs(d.results || []);
        setTotal(d.total || 0);
        setPage(d.page || 1);
        setPages(d.pages || 1);
      })
      .catch(e => { setDocs([]); setLoadErr(e.message || "Error al cargar registros"); })
      .finally(() => setLoading(false));
  }, [condoId, tipo, search]);

  // loadDocs cambia con `search` en cada tecla — si se incluyera acá, este
  // efecto refetchearía en cada tecla de búsqueda además del debounce propio;
  // solo debe recargar al cambiar de condominio o tipo, no en cada tecla de búsqueda.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (condoId) loadDocs(1); }, [condoId, tipo]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  };

  async function handleDelete() {
    if (!selected.size) { showToast("Seleccione al menos un registro", "error"); return; }
    const tipoInfo  = TIPOS_DOC.find(t => t.value === tipo);
    const tipoLabel = tipoInfo?.label || tipo;
    const warningExtra = tipoInfo?.warning ? `\n\n${tipoInfo.warning}` : "";

    if (!window.confirm(
      `¿Eliminar ${selected.size} ${tipoLabel}?\n\nEsta acción es permanente e irreversible.${warningExtra}`
    )) return;

    setDeleting(true);
    try {
      const res  = await apiFetch("/api/core/admin/eliminar-rango/", {
        method: "POST",
        body: JSON.stringify({ condominio_id: condoId, tipo, ids: [...selected] }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Error al eliminar", "error"); return; }
      const errMsg = data.errores?.length ? ` (${data.errores.length} con error)` : "";
      showToast(`${data.eliminados} registro(s) eliminado(s)${errMsg}`);
      setSelected(new Set());
      loadDocs(1);
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setDeleting(false);
    }
  }

  const tipoInfo = TIPOS_DOC.find(t => t.value === tipo);
  const fmtMonto = (n) => n != null ? `₡${Number(n).toLocaleString("en-US")}` : "";

  const cellVal = (doc) => {
    if (tipo === "filiales")       return [doc.numero, doc.concepto, doc.extra || "-"];
    if (tipo === "propietarios")   return [doc.numero, doc.concepto, doc.extra];
    if (tipo === "copropietarios") return [doc.numero, doc.concepto, doc.extra];
    if (tipo === "autorizados")    return [doc.numero, doc.concepto, doc.extra, doc.tipo || "-"];
    if (tipo === "asientos")       return [doc.numero, doc.fecha, doc.concepto, doc.tipo];
    if (tipo === "ingresos")       return [doc.numero, doc.fecha, doc.concepto];
    if (tipo === "gastos")         return [doc.numero, doc.fecha, doc.concepto];
    if (tipo === "movimientos_bancarios") return [doc.numero, doc.fecha, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "facturas")       return [doc.numero, doc.fecha, doc.filial, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos")        return [doc.numero, doc.fecha, doc.filial, fmtMonto(doc.monto)];
    if (tipo === "facturas_cxp")   return [doc.numero, doc.fecha, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos_cxp")    return [doc.numero, doc.fecha, fmtMonto(doc.monto)];
    if (tipo === "proveedores")    return [doc.numero, doc.concepto, doc.extra];
    return [];
  };

  const inpSt = {
    background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
    color: C.text, padding: "9px 14px", fontSize: "0.85rem", outline: "none",
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "", type: "" })} />
      <ErrorBanner msg={loadErr} onRetry={condoId ? () => loadDocs(1) : null} />

      {/* Aviso */}
      <div style={{
        background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)",
        borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: "0.82rem",
        color: "#fca5a5", display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          Esta función elimina registros de forma <strong>permanente e irreversible</strong>.
          Úsela solo cuando el condominio lo solicite explícitamente.
          Los registros relacionados (CxC, asientos, movimientos bancarios) también se eliminan.
        </span>
      </div>

      {/* Filtros */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, marginBottom: 20, alignItems: "end" }}>
        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Condominio
          </label>
          <select value={condoId} onChange={e => { setCondoId(e.target.value); setDocs([]); setSelected(new Set()); }}
            style={{ ...inpSt, width: "100%" }}>
            <option value="">- Seleccionar -</option>
            {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Tipo de registro
          </label>
          <select value={tipo} onChange={e => { setTipo(e.target.value); setDocs([]); setSelected(new Set()); }}
            style={{ ...inpSt, width: "100%" }}>
            {GRUPOS.map(g => (
              <optgroup key={g} label={g}>
                {TIPOS_DOC.filter(t => t.group === g).map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: "0.7rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 5 }}>
            Buscar
          </label>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && loadDocs(1)}
            placeholder="N° documento, nombre, cédula…"
            style={{ ...inpSt, width: "100%", boxSizing: "border-box" }} />
        </div>

        <Btn onClick={() => loadDocs(1)} disabled={!condoId || loading}>
          <i className={`fa-solid fa-${loading ? "circle-notch fa-spin" : "magnifying-glass"}`} />
          Buscar
        </Btn>
      </div>

      {/* Advertencia específica del tipo */}
      {tipoInfo?.warning && (
        <div style={{
          background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.25)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 16,
          fontSize: "0.8rem", color: "#fde68a", display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{tipoInfo.warning}</span>
        </div>
      )}

      {/* Tabla */}
      {docs.length > 0 ? (
        <>
          {/* Barra de acciones */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 12, padding: "10px 14px",
            background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.84rem", color: C.muted }}>
                <input type="checkbox"
                  checked={selected.size === docs.length && docs.length > 0}
                  onChange={toggleAll} />
                Todos ({docs.length} en página)
              </label>
              {selected.size > 0 && (
                <span style={{ color: C.accent, fontSize: "0.84rem", fontWeight: 600 }}>
                  {selected.size} seleccionados
                </span>
              )}
              <span style={{ color: C.muted, fontSize: "0.78rem" }}>
                Total: {total} registros
              </span>
            </div>
            <Btn variant="danger" onClick={handleDelete} disabled={deleting || selected.size === 0}>
              {deleting
                ? <><i className="fa-solid fa-circle-notch fa-spin" />Eliminando…</>
                : <><i className="fa-solid fa-trash" />Eliminar seleccionados ({selected.size})</>}
            </Btn>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                  <th style={{ width: 36, padding: "8px 12px" }} />
                  {tipoInfo?.cols.map(col => (
                    <th key={col} style={{
                      padding: "8px 12px", textAlign: "left",
                      color: C.muted, fontWeight: 700, fontSize: "0.7rem",
                      textTransform: "uppercase", letterSpacing: "0.5px",
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((doc, i) => (
                  <tr key={doc.id}
                    style={{
                      background: selected.has(doc.id)
                        ? "rgba(249,115,22,0.08)"
                        : i % 2 === 0 ? "transparent" : "var(--hover-bg-sm, var(--hover-bg))",
                      borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                    }}
                    onClick={() => toggleSelect(doc.id)}>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      <input type="checkbox" checked={selected.has(doc.id)}
                        onChange={() => toggleSelect(doc.id)}
                        onClick={e => e.stopPropagation()} />
                    </td>
                    {cellVal(doc).map((v, ci) => (
                      <td key={ci} style={{
                        padding: "8px 12px",
                        color: ci === 0 ? tipoInfo?.color || C.accent : C.text,
                        fontFamily: ci === 0 ? "monospace" : "inherit",
                        maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {v || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
              <button onClick={() => loadDocs(page - 1)} disabled={page <= 1}
                style={{ padding: "5px 10px", borderRadius: 6, fontSize: "0.82rem", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, cursor: "pointer", opacity: page <= 1 ? 0.4 : 1 }}>
                ‹
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 2)
                .map((p, idx, arr) => (
                  <>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span key={`ellipsis-${p}`} style={{ padding: "5px 4px", color: C.muted, fontSize: "0.82rem" }}>…</span>
                    )}
                    <button key={p} onClick={() => loadDocs(p)}
                      style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: "0.82rem",
                        background: p === page ? C.accent : "transparent",
                        color: p === page ? "#fff" : C.muted,
                        border: `1px solid ${p === page ? C.accent : C.border}`,
                        cursor: "pointer",
                      }}>
                      {p}
                    </button>
                  </>
                ))}
              <button onClick={() => loadDocs(page + 1)} disabled={page >= pages}
                style={{ padding: "5px 10px", borderRadius: 6, fontSize: "0.82rem", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, cursor: "pointer", opacity: page >= pages ? 0.4 : 1 }}>
                ›
              </button>
            </div>
          )}
        </>
      ) : condoId && !loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-inbox" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
          <p style={{ margin: 0 }}>No hay registros. Presione <strong>Buscar</strong> para cargar.</p>
        </div>
      ) : !condoId ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-building" style={{ fontSize: "2rem", display: "block", marginBottom: 10, opacity: 0.2 }} />
          <p style={{ margin: 0 }}>Seleccione un condominio para ver sus registros.</p>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: C.accent, fontSize: "1.5rem" }} />
        </div>
      )}
    </div>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────────
// ── Tab Backups ───────────────────────────────────────────────────────────────
function BackupsTab() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [running,  setRunning]  = useState(false);
  const [msg,      setMsg]      = useState({ text: "", ok: true });

  // Backup por condominio
  const [condominios, setCondominios] = useState([]);
  const [selCondo,    setSelCondo]    = useState("");
  const [condoRun,    setCondoRun]    = useState(false);

  // Descarga y restauración
  const [descargando, setDescargando] = useState(null);   // nombre en curso
  const [restoreTgt,  setRestoreTgt]  = useState(null);    // {mode, nombre?, file?}
  const [restoreTxt,  setRestoreTxt]  = useState("");
  const [restoreRun,  setRestoreRun]  = useState(false);
  const [limpiar,     setLimpiar]     = useState(false);   // import condominio: reemplazo limpio
  const fileRef = useRef(null);

  // Cada vez que se abre el modal de restauración, arrancar sin "reemplazo limpio".
  useEffect(() => { if (restoreTgt) setLimpiar(false); }, [restoreTgt]);

  async function cargar() {
    setLoading(true);
    try {
      const r = await apiFetch("/api/core/admin/backups/");
      setData(await r.json());
    } catch { setMsg({ text: "Error al cargar backups", ok: false }); }
    finally { setLoading(false); }
  }

  async function cargarCondominios() {
    try {
      const r = await apiFetch("/api/core/admin/condominios/");
      const j = await r.json();
      setCondominios(Array.isArray(j) ? j : (j.results ?? []));
    } catch { /* noop */ }
  }

  useEffect(() => { cargar(); cargarCondominios(); }, []);

  function flash(text, ok = true) {
    setMsg({ text, ok });
    if (ok) setTimeout(() => setMsg({ text: "", ok: true }), 5000);
  }

  async function handleBackupAhora() {
    if (!window.confirm("¿Generar un backup ahora? Puede tomar unos segundos.")) return;
    setRunning(true);
    setMsg({ text: "", ok: true });
    try {
      const r = await apiFetch("/api/core/admin/backups/", { method: "POST" });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || "Error");
      flash("✅ Backup generado correctamente", true);
      await cargar();
    } catch(e) { flash(e.message, false); }
    finally { setRunning(false); }
  }

  async function handleCondoBackup() {
    if (!selCondo) { flash("Seleccioná un condominio.", false); return; }
    setCondoRun(true);
    setMsg({ text: "", ok: true });
    try {
      const r = await apiFetch(`/api/core/admin/condominios/${selCondo}/backup/`, { method: "POST" });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || json.detail || "Error");
      flash(`✅ Backup de "${json.condominio}" generado (${json.registros} registros).`, true);
      await cargar();
    } catch(e) { flash(e.message, false); }
    finally { setCondoRun(false); }
  }

  async function descargar(nombre) {
    setDescargando(nombre);
    try {
      const r = await apiFetch(
        `/api/core/admin/backups/download/?nombre=${encodeURIComponent(nombre)}`
      );
      if (!r.ok) throw new Error("No se pudo descargar el archivo.");
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = nombre;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch(e) { flash(e.message, false); }
    finally { setDescargando(null); }
  }

  async function renombrar(b) {
    const sugerido = b.tipo === "condominio" ? (b.condominio_label || "") : "";
    const nuevo = window.prompt(
      "Nuevo nombre para el backup (letras, números y espacios).\n" +
      "Se conserva el tipo y la extensión automáticamente:",
      sugerido
    );
    if (nuevo == null || !nuevo.trim()) return;
    try {
      const r = await apiFetch("/api/core/admin/backups/rename/", {
        method: "POST",
        body: JSON.stringify({ nombre: b.nombre, nuevo: nuevo.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "No se pudo renombrar");
      flash(`✅ Renombrado a "${j.nombre}".`, true);
      await cargar();
    } catch(e) { flash(e.message, false); }
  }

  function pedirRestoreArchivo() {
    fileRef.current?.click();
  }

  function onArchivoElegido(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.name.endsWith(".sql.gz")) {
      setRestoreTxt("");
      setRestoreTgt({ mode: "file", file });          // backup completo
    } else if (file.name.endsWith(".json.gz")) {
      setRestoreTxt("");
      setRestoreTgt({ mode: "file-condo", file });    // backup de un condominio
    } else {
      flash("El archivo debe ser un backup .sql.gz (completo) o .json.gz (condominio).", false);
    }
  }

  async function ejecutarRestore() {
    if (restoreTxt.trim() !== "RESTAURAR") return;
    const esCondo = restoreTgt.mode === "file-condo" || restoreTgt.mode === "server-condo";
    const endpoint = esCondo
      ? "/api/core/admin/condominios/restore/"
      : "/api/core/admin/backups/restore/";
    // "Reemplazo limpio" solo aplica al import de un condominio (.json.gz).
    const limpiarReq = esCondo && limpiar;
    setRestoreRun(true);
    try {
      let r;
      if (restoreTgt.mode === "file" || restoreTgt.mode === "file-condo") {
        const fd = new FormData();
        fd.append("archivo", restoreTgt.file);
        fd.append("confirmacion", "RESTAURAR");
        if (esCondo) fd.append("limpiar", limpiarReq ? "true" : "false");
        r = await fetch(`${BACKUP_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: getAuthHeaders(),   // sin Content-Type → el navegador pone el boundary
          body: fd,
        });
      } else {
        r = await apiFetch(endpoint, {
          method: "POST",
          body: JSON.stringify({
            nombre: restoreTgt.nombre,
            confirmacion: "RESTAURAR",
            ...(esCondo ? { limpiar: limpiarReq } : {}),
          }),
        });
      }
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || json.detail || "Error al restaurar");
      setRestoreTgt(null);
      flash(
        esCondo
          ? `✅ Condominio ${limpiarReq ? "reemplazado" : "importado"} correctamente${json.registros ? ` (${json.registros} registros)` : ""}.`
          : "✅ Base restaurada correctamente. Se generó un backup de seguridad previo.",
        true,
      );
      await cargar();
    } catch(e) { flash(e.message, false); }
    finally { setRestoreRun(false); }
  }

  const rowSt = { padding: "11px 14px", fontSize: "0.83rem", color: C.text,
    borderBottom: `1px solid ${C.border}` };
  const thSt  = { padding: "9px 14px", fontSize: "0.7rem", fontWeight: 700,
    letterSpacing: "0.5px", textTransform: "uppercase", color: C.muted,
    borderBottom: `1px solid ${C.border}` };
  const miniBtn = (bg, brd, col) => ({
    background: bg, border: `1px solid ${brd}`, color: col, borderRadius: 6,
    padding: "4px 10px", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 5,
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: C.text, fontSize: "0.95rem" }}>Backups de base de datos</h3>
          <p style={{ margin: "4px 0 0", color: C.muted, fontSize: "0.78rem" }}>
            Directorio: <code style={{ color: "#60a5fa" }}>{data?.directorio || "…"}</code>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={pedirRestoreArchivo}
            style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.text,
              borderRadius: 9, padding: "8px 16px", fontSize: "0.84rem", fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
            <i className="fa-solid fa-upload"/>Restaurar desde archivo
          </button>
          <button onClick={handleBackupAhora} disabled={running}
            style={{ background: C.accent, border: "none", color: "#fff", borderRadius: 9,
              padding: "8px 18px", fontSize: "0.84rem", fontWeight: 700,
              cursor: running ? "not-allowed" : "pointer", opacity: running ? 0.7 : 1,
              display: "flex", alignItems: "center", gap: 7 }}>
            {running
              ? <><i className="fa-solid fa-circle-notch fa-spin"/>Generando…</>
              : <><i className="fa-solid fa-database"/>Backup ahora</>}
          </button>
        </div>
      </div>

      <input ref={fileRef} type="file" accept=".gz" style={{ display: "none" }}
        onChange={onArchivoElegido}/>

      {msg.text && (
        <div style={{ padding: "9px 14px", borderRadius: 8, marginBottom: 14, fontSize: "0.82rem",
          background: msg.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
          border: `1px solid ${msg.ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
          color: msg.ok ? "#4ade80" : "#f87171" }}>
          {msg.text}
        </div>
      )}

      {/* Backup por condominio */}
      <div style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)",
        borderRadius: 9, padding: "12px 16px", marginBottom: 16 }}>
        <p style={{ margin: "0 0 8px", color: "#a78bfa", fontWeight: 700, fontSize: "0.8rem" }}>
          <i className="fa-solid fa-building-shield" style={{ marginRight: 6 }}/>Backup de un condominio específico
        </p>
        <p style={{ margin: "0 0 10px", color: C.muted, fontSize: "0.76rem" }}>
          Exporta todos los datos de un solo condominio (contabilidad, residentes, proveedores,
          portal) a un archivo <code>.json.gz</code>. Funciona en cualquier base.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={selCondo} onChange={e => setSelCondo(e.target.value)}
            style={{ background: C.bg2, border: `1px solid ${C.border}`, color: C.text,
              borderRadius: 8, padding: "8px 12px", fontSize: "0.83rem", minWidth: 240 }}>
            <option value="">Seleccioná un condominio…</option>
            {condominios.map(c => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
          <button onClick={handleCondoBackup} disabled={condoRun || !selCondo}
            style={{ background: "#8b5cf6", border: "none", color: "#fff", borderRadius: 8,
              padding: "8px 16px", fontSize: "0.83rem", fontWeight: 700,
              cursor: (condoRun || !selCondo) ? "not-allowed" : "pointer",
              opacity: (condoRun || !selCondo) ? 0.6 : 1,
              display: "flex", alignItems: "center", gap: 7 }}>
            {condoRun
              ? <><i className="fa-solid fa-circle-notch fa-spin"/>Generando…</>
              : <><i className="fa-solid fa-file-zipper"/>Generar backup del condominio</>}
          </button>
        </div>
      </div>

      {/* Instrucciones cron */}
      <div style={{ background: "rgba(96,165,250,0.06)", border: "1px solid rgba(96,165,250,0.15)",
        borderRadius: 9, padding: "12px 16px", marginBottom: 16 }}>
        <p style={{ margin: "0 0 6px", color: "#60a5fa", fontWeight: 700, fontSize: "0.8rem" }}>
          <i className="fa-solid fa-clock" style={{ marginRight: 6 }}/>Programar backup automático (cron)
        </p>
        <p style={{ margin: "0 0 6px", color: C.muted, fontSize: "0.76rem" }}>
          Agrega una de estas líneas con <code>crontab -e</code> en el servidor:
        </p>
        <code style={{ display: "block", background: "rgba(0,0,0,0.3)", borderRadius: 6,
          padding: "8px 12px", fontSize: "0.73rem", color: "#e2e8f0", marginBottom: 4,
          wordBreak: "break-all" }}>
          # Mensual (día 1 a las 3am){"\n"}
          0 3 1 * * /home/appuser/Facturacion_Staging/backend/venv/bin/python /home/appuser/Facturacion_Staging/backend/manage.py backup_db
        </code>
        <code style={{ display: "block", background: "rgba(0,0,0,0.3)", borderRadius: 6,
          padding: "8px 12px", fontSize: "0.73rem", color: "#e2e8f0", wordBreak: "break-all" }}>
          # Quincenal (días 1 y 15 a las 3am){"\n"}
          0 3 1,15 * * /home/appuser/Facturacion_Staging/backend/venv/bin/python /home/appuser/Facturacion_Staging/backend/manage.py backup_db
        </code>
      </div>

      {/* Lista de backups */}
      {loading ? (
        <p style={{ color: C.muted, fontSize: "0.85rem" }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }}/>Cargando…
        </p>
      ) : !data?.backups?.length ? (
        <p style={{ color: C.muted, fontSize: "0.85rem", textAlign: "center", padding: "30px 0" }}>
          No hay backups aún. Usa el botón "Backup ahora" o configura el cron.
        </p>
      ) : (
        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thSt, textAlign: "left" }}>Archivo</th>
                <th style={thSt}>Tipo</th>
                <th style={thSt}>Fecha</th>
                <th style={thSt}>Tamaño</th>
                <th style={thSt}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.backups.map((b, i) => {
                const esCompleto = b.tipo === "completo";
                return (
                <tr key={b.nombre}>
                  <td style={rowSt}>
                    <i className={`fa-solid ${esCompleto ? "fa-database" : "fa-building"}`}
                      style={{ color: esCompleto ? C.accent : "#a78bfa", marginRight: 8 }}/>
                    {b.nombre}
                    {i === 0 && (
                      <span style={{ marginLeft: 8, fontSize: "0.7rem", background: "rgba(249,115,22,0.15)",
                        color: C.accent, borderRadius: 4, padding: "1px 6px" }}>más reciente</span>
                    )}
                  </td>
                  <td style={{ ...rowSt, textAlign: "center" }}>
                    {esCompleto ? (
                      <span style={{ fontSize: "0.72rem", color: C.accent, fontWeight: 600 }}>Completo</span>
                    ) : (
                      <span style={{ fontSize: "0.72rem", color: "#a78bfa", fontWeight: 600 }}
                        title={b.condominio_label || ""}>
                        Condominio
                      </span>
                    )}
                  </td>
                  <td style={{ ...rowSt, textAlign: "center", color: C.muted }}>
                    {new Date(b.fecha).toLocaleString("es-CR")}
                  </td>
                  <td style={{ ...rowSt, textAlign: "center" }}>
                    <span style={{ color: "#4ade80", fontWeight: 600 }}>{b.tamano}</span>
                  </td>
                  <td style={{ ...rowSt, textAlign: "center", whiteSpace: "nowrap" }}>
                    <button onClick={() => descargar(b.nombre)} disabled={descargando === b.nombre}
                      style={miniBtn("rgba(96,165,250,0.12)", "rgba(96,165,250,0.35)", "#60a5fa")}>
                      {descargando === b.nombre
                        ? <i className="fa-solid fa-circle-notch fa-spin"/>
                        : <><i className="fa-solid fa-download"/>Descargar</>}
                    </button>
                    <button onClick={() => renombrar(b)} title="Cambiar el nombre del archivo"
                      style={{ ...miniBtn("rgba(148,163,184,0.12)", "rgba(148,163,184,0.35)", "#cbd5e1"), marginLeft: 6 }}>
                      <i className="fa-solid fa-pen"/>Renombrar
                    </button>
                    {esCompleto ? (
                      <button onClick={() => { setRestoreTxt(""); setRestoreTgt({ mode: "server", nombre: b.nombre }); }}
                        style={{ ...miniBtn("rgba(239,68,68,0.12)", "rgba(239,68,68,0.35)", "#f87171"), marginLeft: 6 }}>
                        <i className="fa-solid fa-rotate-left"/>Restaurar
                      </button>
                    ) : (
                      <button onClick={() => { setRestoreTxt(""); setRestoreTgt({ mode: "server-condo", nombre: b.nombre, label: b.condominio_label }); }}
                        style={{ ...miniBtn("rgba(167,139,250,0.12)", "rgba(167,139,250,0.35)", "#a78bfa"), marginLeft: 6 }}>
                        <i className="fa-solid fa-rotate-left"/>Importar
                      </button>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal restaurar */}
      {restoreTgt && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center" }}
          {...overlayClose(() => !restoreRun && setRestoreTgt(null))}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12,
              padding: 24, width: "min(520px,94vw)", boxShadow: "0 24px 64px rgba(0,0,0,0.55)" }}>
            {(() => {
              const esCondo = restoreTgt.mode === "file-condo" || restoreTgt.mode === "server-condo";
              const nombreArchivo = restoreTgt.file ? restoreTgt.file.name : restoreTgt.nombre;
              const col = esCondo ? "#a78bfa" : "#f87171";
              return (
                <>
                  <h3 style={{ margin: "0 0 6px", color: col, fontSize: "1rem", fontWeight: 700 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }}/>
                    {esCondo ? "Importar backup de condominio" : "Restaurar base de datos"}
                  </h3>
                  <div style={{ background: esCondo ? "rgba(167,139,250,0.08)" : "rgba(239,68,68,0.08)",
                    border: `1px solid ${esCondo ? "rgba(167,139,250,0.3)" : "rgba(239,68,68,0.3)"}`,
                    borderRadius: 8, padding: "10px 14px", margin: "10px 0",
                    color: esCondo ? "#c4b5fd" : "#fca5a5", fontSize: "0.83rem" }}>
                    {!esCondo ? (
                      <>Esto <strong>reemplaza TODOS los datos actuales</strong> con el contenido de:</>
                    ) : limpiar ? (
                      <>Esto <strong>borra y reemplaza los datos de este condominio</strong> con el contenido de:</>
                    ) : (
                      <>Esto <strong>sobrescribe (merge por ID) los datos de este condominio</strong> con el contenido de:</>
                    )}
                    <div style={{ marginTop: 6, fontFamily: "monospace",
                      color: esCondo ? "#ddd6fe" : "#fecaca", wordBreak: "break-all" }}>
                      {nombreArchivo}
                    </div>
                    <div style={{ marginTop: 8, color: (esCondo && limpiar) ? "#fca5a5" : "#fde68a" }}>
                      <i className={`fa-solid ${(esCondo && limpiar) ? "fa-trash-can" : "fa-shield-halved"}`}
                        style={{ marginRight: 6 }}/>
                      {!esCondo
                        ? "Se generará un backup de seguridad de la base actual antes de restaurar."
                        : limpiar
                          ? "Reemplazo limpio: se BORRARÁN todos los datos actuales de este condominio y se cargarán los del archivo. Si la carga falla, el borrado se revierte automáticamente."
                          : "Los registros se cargan por ID (no borra datos previos). Activá el reemplazo limpio para que el condominio quede idéntico al archivo."}
                    </div>
                  </div>
                  {esCondo && (
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "0 0 12px",
                      padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                      background: limpiar ? "rgba(239,68,68,0.08)" : "rgba(148,163,184,0.06)",
                      border: `1px solid ${limpiar ? "rgba(239,68,68,0.35)" : C.border}` }}>
                      <input type="checkbox" checked={limpiar} onChange={e => setLimpiar(e.target.checked)}
                        style={{ marginTop: 2, accentColor: "#ef4444", cursor: "pointer" }}/>
                      <span style={{ fontSize: "0.8rem", color: C.text }}>
                        <strong>Reemplazo limpio</strong> - borrar todo lo que el condominio tiene
                        ahora y dejarlo idéntico al archivo (recomendado para restaurar una copia).
                        <span style={{ display: "block", color: C.muted, marginTop: 2 }}>
                          Sin esta opción, los datos del archivo solo se mezclan/sobrescriben por ID.
                        </span>
                      </span>
                    </label>
                  )}
                </>
              );
            })()}
            <label style={{ display: "block", color: C.muted, fontSize: "0.8rem", marginBottom: 6 }}>
              Escribí <strong style={{ color: C.text }}>RESTAURAR</strong> para confirmar:
            </label>
            <input value={restoreTxt} onChange={e => setRestoreTxt(e.target.value)} autoFocus
              placeholder="RESTAURAR"
              style={{ width: "100%", background: C.bg2, border: `1px solid ${C.border}`, color: C.text,
                borderRadius: 8, padding: "9px 12px", fontSize: "0.9rem", boxSizing: "border-box" }}/>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button onClick={() => setRestoreTgt(null)} disabled={restoreRun}
                style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${C.border}`,
                  borderRadius: 8, color: C.muted, fontSize: "0.84rem", cursor: "pointer" }}>
                Cancelar
              </button>
              {(() => {
                const esCondo = restoreTgt.mode === "file-condo" || restoreTgt.mode === "server-condo";
                const listo = restoreTxt.trim() === "RESTAURAR";
                // El reemplazo limpio es destructivo → rojo, como el restore completo.
                const destructivo = !esCondo || limpiar;
                const baseCol = destructivo ? "#ef4444" : "#8b5cf6";
                const offCol  = destructivo ? "rgba(239,68,68,0.4)" : "rgba(139,92,246,0.4)";
                const verbo = !esCondo ? "Restaurar" : limpiar ? "Reemplazar" : "Importar";
                return (
                  <button onClick={ejecutarRestore} disabled={restoreRun || !listo}
                    style={{ padding: "8px 18px", background: listo ? baseCol : offCol,
                      color: "#fff", border: "none", borderRadius: 8, fontSize: "0.84rem", fontWeight: 700,
                      cursor: (restoreRun || !listo) ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 7 }}>
                    {restoreRun
                      ? <><i className="fa-solid fa-circle-notch fa-spin"/>{`${verbo === "Restaurar" ? "Restaurando" : verbo === "Reemplazar" ? "Reemplazando" : "Importando"}…`}</>
                      : <><i className="fa-solid fa-rotate-left"/>{`${verbo} ahora`}</>}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Mantenimiento Tab ─────────────────────────────────────────────────────────
// Los diagnósticos vivían solo como comandos de consola en el servidor. Acá se
// pueden correr sin entrar por SSH, y cada uno explica qué revisa antes de
// ejecutarse.
function MantenimientoTab() {
  const [catalogo, setCatalogo]   = useState([]);
  const [condos, setCondos]       = useState([]);
  const [sel, setSel]             = useState(null);       // comando seleccionado
  const [condo, setCondo]         = useState("");
  const [opciones, setOpciones]   = useState({});
  const [corriendo, setCorriendo] = useState(false);
  const [salida, setSalida]       = useState(null);
  const [error, setError]         = useState("");

  useEffect(() => {
    apiFetch("/api/core/admin/mantenimiento/")
      .then(r => r.json())
      .then(d => { setCatalogo(d.comandos ?? []); setCondos(d.condominios ?? []); })
      .catch(() => setError("No se pudo cargar la lista de herramientas."));
  }, []);

  function elegir(cmd) {
    setSel(cmd);
    setOpciones({});
    setSalida(null);
    setError("");
  }

  // Escribe en la base si el comando escribe por defecto y no se activó una
  // opción de solo lectura, o si se activó una opción que sí escribe.
  function vaAEscribir() {
    if (!sel) return false;
    const soloLectura = (sel.opciones || []).some(
      o => !o.escribe && opciones[o.flag]);
    const escribeOpcion = (sel.opciones || []).some(
      o => o.escribe && opciones[o.flag]);
    return escribeOpcion || (sel.escribe && !soloLectura);
  }

  async function ejecutar() {
    if (!sel) return;
    if (vaAEscribir() &&
        !window.confirm(
          `"${sel.titulo}" va a modificar datos.\n\n` +
          `¿Seguro que querés continuar?`)) return;
    setCorriendo(true); setSalida(null); setError("");
    try {
      const res = await apiFetch("/api/core/admin/mantenimiento/", {
        method: "POST",
        body: JSON.stringify({
          comando: sel.nombre,
          condominio: condo || null,
          opciones,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Error al ejecutar");
      setSalida(d);
    } catch (e) {
      setError(e.message || "Error al ejecutar la herramienta");
    } finally {
      setCorriendo(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{
        background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.25)",
        borderLeft: "3px solid #3b82f6", borderRadius: 8, padding: "12px 16px",
        fontSize: "0.85rem", color: "var(--text-secondary)",
      }}>
        <i className="fa-solid fa-circle-info" style={{ color: "#60a5fa", marginRight: 8 }} />
        Herramientas de revisión y reparación de datos. Las que solo leen se pueden
        correr sin riesgo; las que escriben piden confirmación y muestran exactamente
        qué tocaron.
      </div>

      {error && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: "0.85rem",
        }}>{error}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
        {catalogo.map(cmd => {
          const activo = sel?.nombre === cmd.nombre;
          return (
            <button key={cmd.nombre} type="button" onClick={() => elegir(cmd)}
              style={{
                textAlign: "left", padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                background: activo ? "rgba(249,115,22,0.08)" : "var(--bg-card)",
                border: `1px solid ${activo ? "rgba(249,115,22,0.5)" : "var(--border-card)"}`,
                color: "var(--text-primary)",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <i className={`fa-solid ${cmd.escribe ? "fa-wrench" : "fa-magnifying-glass"}`}
                   style={{ color: cmd.escribe ? "#fbbf24" : "#60a5fa", fontSize: "0.85rem" }} />
                <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>{cmd.titulo}</span>
              </div>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", lineHeight: 1.45 }}>
                {cmd.resumen}
              </div>
            </button>
          );
        })}
      </div>

      {sel && (
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, padding: "18px 20px", display: "flex",
          flexDirection: "column", gap: 14,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>
              {sel.titulo}
            </h3>
            <p style={{
              margin: "8px 0 0", fontSize: "0.85rem", color: "var(--text-secondary)",
              whiteSpace: "pre-line", lineHeight: 1.55,
            }}>{sel.detalle}</p>
          </div>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
            {sel.acepta_condominio && (
              <label style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                Condominio
                <select value={condo} onChange={e => setCondo(e.target.value)}
                  style={{
                    display: "block", marginTop: 4, minWidth: 220, padding: "7px 10px",
                    background: "var(--bg-input)", color: "var(--text-primary)",
                    border: "1px solid var(--border-card)", borderRadius: 6,
                  }}>
                  <option value="">Todos</option>
                  {condos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </label>
            )}
          </div>

          {(sel.opciones || []).map(op => (
            <label key={op.flag} style={{
              display: "flex", gap: 10, alignItems: "flex-start", fontSize: "0.84rem",
              color: "var(--text-secondary)", cursor: "pointer",
            }}>
              <input type="checkbox" checked={!!opciones[op.flag]} style={{ marginTop: 3 }}
                onChange={e => setOpciones(o => ({ ...o, [op.flag]: e.target.checked }))} />
              <span>
                <strong style={{ color: "var(--text-primary)" }}>{op.etiqueta}</strong>
                {op.escribe && (
                  <span style={{ color: "#fbbf24", marginLeft: 6, fontSize: "0.76rem" }}>
                    modifica datos
                  </span>
                )}
                <span style={{ display: "block", color: "var(--text-muted)", marginTop: 2 }}>
                  {op.ayuda}
                </span>
              </span>
            </label>
          ))}

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" onClick={ejecutar} disabled={corriendo}
              style={{
                padding: "9px 18px", borderRadius: 8, border: "none", cursor: corriendo ? "wait" : "pointer",
                background: vaAEscribir()
                  ? "linear-gradient(135deg,#d97706,#f59e0b)"
                  : "linear-gradient(135deg,#f97316,#fb923c)",
                color: "#fff", fontWeight: 700, fontSize: "0.87rem",
              }}>
              {corriendo ? "Ejecutando…" : vaAEscribir() ? "Ejecutar y corregir" : "Ejecutar"}
            </button>
            {vaAEscribir() && (
              <span style={{ fontSize: "0.8rem", color: "#fbbf24" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                Esta corrida modifica datos
              </span>
            )}
          </div>
        </div>
      )}

      {salida && (
        <div style={{
          background: "var(--bg-card)", border: `1px solid ${salida.ok ? "var(--border-card)" : "rgba(239,68,68,0.4)"}`,
          borderRadius: 12, padding: "16px 18px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <i className={`fa-solid ${salida.ok ? (salida.con_hallazgos ? "fa-triangle-exclamation" : "fa-circle-check") : "fa-circle-xmark"}`}
               style={{ color: salida.ok ? (salida.con_hallazgos ? "#fbbf24" : "#4ade80") : "#f87171" }} />
            <strong style={{ color: "var(--text-primary)", fontSize: "0.9rem" }}>
              {!salida.ok ? "La herramienta falló"
                : salida.con_hallazgos ? "Terminó y encontró cosas para revisar"
                : "Terminó sin encontrar problemas"}
            </strong>
          </div>
          {salida.error && (
            <div style={{ color: "#f87171", fontSize: "0.82rem", marginBottom: 8 }}>{salida.error}</div>
          )}
          <pre style={{
            margin: 0, maxHeight: 460, overflow: "auto", fontSize: "0.78rem",
            lineHeight: 1.5, color: "var(--text-secondary)", whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, 'Cascadia Code', Consolas, monospace",
          }}>{salida.salida || "(sin salida)"}</pre>
        </div>
      )}
    </div>
  );
}

// ── Novedades ────────────────────────────────────────────────────────────────
// Quién ve el modal de "¿Qué hay de nuevo?" y qué versiones están publicadas.
// El contenido de cada versión sigue viniendo de frontend/src/shared/data/
// changelog.js (con su audiencia por item); acá se decide quién puede abrirlo
// y qué releases todavía no se anuncian. El superadministrador siempre lo ve
// todo, para poder revisar antes de publicar.

const ROL_LABEL = {
  USUARIO:   { nombre: "Administración del condominio", ayuda: "Los usuarios que manejan el condominio desde el sistema" },
  CONTADOR:  { nombre: "Contadores",                    ayuda: "Usuarios multi-condominio con acceso contable" },
  RESIDENTE: { nombre: "Residentes (Portal)",           ayuda: "Los condóminos que entran al portal. Lo que vean sale solo de los items marcados para ellos" },
  AGUA:      { nombre: "Operadores de agua",            ayuda: "Solo usan el módulo de lecturas" },
  SEGURIDAD: { nombre: "Oficiales de seguridad",        ayuda: "Solo usan la app de Seguridad" },
};

function NovedadesTab() {
  const [cfg, setCfg]         = useState(null);
  const [versiones, setVers]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [error, setError]     = useState("");

  const cargar = useCallback(() => {
    setLoading(true); setError("");
    apiFetch("/api/core/novedades/config/")
      .then(async r => {
        if (!r.ok) throw new Error(`No se pudo cargar la configuración (${r.status}).`);
        return r.json();
      })
      .then(setCfg)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // El catálogo de versiones sale del propio changelog del frontend: es la
  // única fuente de qué releases existen.
  useEffect(() => {
    import("../../../shared/data/changelog")
      .then(m => setVers((m.default || []).map(v => ({ version: v.version, title: v.title, date: v.date }))))
      .catch(() => setVers([]));
  }, []);

  function toggleRol(rol) {
    setCfg(c => {
      const actuales = c.roles_visibles || [];
      return { ...c, roles_visibles: actuales.includes(rol)
        ? actuales.filter(r => r !== rol)
        : [...actuales, rol] };
    });
  }

  function togglePublicada(version) {
    setCfg(c => {
      const ocultas = c.versiones_ocultas || [];
      return { ...c, versiones_ocultas: ocultas.includes(version)
        ? ocultas.filter(v => v !== version)
        : [...ocultas, version] };
    });
  }

  async function guardar() {
    setSaving(true); setToast(null);
    try {
      const r = await apiFetch("/api/core/novedades/config/", {
        method: "PATCH",
        body: JSON.stringify({
          roles_visibles:    cfg.roles_visibles || [],
          versiones_ocultas: cfg.versiones_ocultas || [],
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || Object.values(d).flat().join(". ") || "No se pudo guardar.");
      setCfg(d);
      // La caché local es lo que leen el botón de regalo y el modal.
      try { localStorage.setItem("novedades_config", JSON.stringify(d)); } catch { /* noop */ }
      setToast({ msg: "Configuración guardada.", type: "ok" });
    } catch (e) {
      setToast({ msg: e.message, type: "error" });
    } finally { setSaving(false); }
  }

  if (loading) {
    return <div style={{ padding: 30, color: C.muted, fontSize: "0.86rem" }}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} /> Cargando…
    </div>;
  }
  if (error) return <ErrorBanner msg={error} onRetry={cargar} />;
  if (!cfg) return null;

  const roles    = cfg.roles_posibles || [];
  const visibles = cfg.roles_visibles || [];
  const ocultas  = cfg.versiones_ocultas || [];

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />

      <p style={{ margin: "0 0 20px", fontSize: "0.85rem", color: C.muted, lineHeight: 1.6, maxWidth: 760 }}>
        El modal de Novedades se abre solo una vez por versión y queda a mano en el botón de
        regalo del encabezado. Acá decidís <strong style={{ color: C.text }}>quién puede verlo</strong> y{" "}
        <strong style={{ color: C.text }}>qué versiones ya están publicadas</strong>. A quien no esté
        marcado no le aparece ni el botón. Vos lo ves siempre, aunque esté todo apagado, para poder
        revisar antes de publicar.
      </p>

      {/* ── Quién lo ve ── */}
      <div style={{ marginBottom: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
          <i className="fa-solid fa-users" style={{ color: C.accent, fontSize: "0.82rem" }} />
          <span style={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.7px",
            textTransform: "uppercase", color: C.muted }}>Quién puede ver las novedades</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 10 }}>
          {roles.map(rol => {
            const marcado = visibles.includes(rol);
            const info = ROL_LABEL[rol] || { nombre: rol, ayuda: "" };
            return (
              <label key={rol} style={{
                display: "flex", alignItems: "flex-start", gap: 11, cursor: "pointer",
                padding: "13px 15px", borderRadius: 11,
                border: `1px solid ${marcado ? "rgba(249,115,22,0.4)" : C.border}`,
                background: marcado ? "rgba(249,115,22,0.06)" : "transparent",
                transition: "all 0.15s",
              }}>
                <input type="checkbox" checked={marcado} onChange={() => toggleRol(rol)}
                  style={{ marginTop: 3, accentColor: C.accent, width: 15, height: 15 }} />
                <span>
                  <span style={{ display: "block", fontSize: "0.85rem", fontWeight: 700, color: C.text }}>
                    {info.nombre}
                  </span>
                  <span style={{ fontSize: "0.76rem", color: C.muted, lineHeight: 1.5 }}>{info.ayuda}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Qué versiones están publicadas ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
          <i className="fa-solid fa-tag" style={{ color: C.accent, fontSize: "0.82rem" }} />
          <span style={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.7px",
            textTransform: "uppercase", color: C.muted }}>Versiones publicadas</span>
        </div>
        {versiones.length === 0 ? (
          <p style={{ margin: 0, fontSize: "0.84rem", color: C.muted }}>No se pudo leer el changelog.</p>
        ) : (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 11, overflow: "hidden" }}>
            {versiones.map((v, i) => {
              const publicada = !ocultas.includes(v.version);
              return (
                <label key={v.version} style={{
                  display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
                  padding: "12px 16px", background: i % 2 ? "var(--hover-bg)" : "transparent",
                  borderBottom: i < versiones.length - 1 ? `1px solid ${C.border}` : "none",
                }}>
                  <input type="checkbox" checked={publicada} onChange={() => togglePublicada(v.version)}
                    style={{ accentColor: C.accent, width: 15, height: 15, flexShrink: 0 }} />
                  <span style={{
                    fontSize: "0.76rem", fontWeight: 700, color: C.accent, flexShrink: 0,
                    background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)",
                    borderRadius: 20, padding: "2px 10px",
                  }}>v{v.version}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: "0.83rem", color: C.text,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v.title}
                  </span>
                  <span style={{ fontSize: "0.76rem", color: C.muted, flexShrink: 0 }}>{v.date}</span>
                  <span style={{
                    fontSize: "0.72rem", fontWeight: 700, flexShrink: 0, minWidth: 86, textAlign: "right",
                    color: publicada ? "#4ade80" : C.muted,
                  }}>
                    {publicada ? "Publicada" : "Sin publicar"}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <Btn onClick={guardar} disabled={saving}>
        {saving
          ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
          : <><i className="fa-solid fa-floppy-disk" /> Guardar</>}
      </Btn>
    </div>
  );
}

const TABS = [
  { key: "condominios", label: "Condominios",  icon: "fa-building"    },
  { key: "usuarios",    label: "Usuarios",     icon: "fa-users"       },
  { key: "contadores",  label: "Contadores",   icon: "fa-calculator"  },
  { key: "agua",        label: "Op. de Agua",  icon: "fa-droplet"     },
  { key: "seguridad",   label: "Seguridad",    icon: "fa-shield-halved" },
  { key: "documentos",  label: "Eliminar Docs", icon: "fa-trash-can"  },
  { key: "backups",     label: "Backups",       icon: "fa-database"   },
  { key: "mantenimiento", label: "Mantenimiento", icon: "fa-screwdriver-wrench" },
  { key: "novedades",   label: "Novedades",     icon: "fa-gift"        },
  { key: "demo",        label: "Demo comercial", icon: "fa-eye"       },
];

export default function AdminPage() {
  const [tab, setTab] = useState("condominios");
  const activeTab = TABS.find(t => t.key === tab);

  return (
    <div>
      <span style={{ color: "var(--text-muted)" }}>
        <Link to="/"><span style={{ color: "var(--text-muted)" }}>Inicio </span></Link>/{" "}
        <span style={{ color: "var(--text-primary)" }}>Admin</span>
      </span>

      {/* Hero — mismo lenguaje visual que el Dashboard principal */}
      <div style={{
        background: "linear-gradient(135deg, var(--bg-card) 0%, var(--bg) 100%)",
        border: "1px solid var(--border-card)", borderRadius: 14,
        padding: "18px 24px", marginTop: 8, marginBottom: 20,
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "radial-gradient(circle, rgba(249,115,22,0.08) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)",
        }} />
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0, position: "relative",
          background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className="fa-solid fa-shield-halved" style={{ color: "#f97316", fontSize: "1.4rem" }} />
        </div>
        <div style={{ position: "relative" }}>
          <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.3px" }}>
            Panel de Administración
          </h1>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.85rem" }}>
            Gestión global de condominios y usuarios - solo accesible para superadministradores
          </p>
        </div>
      </div>

      {/* Tabs — pill activo con gradiente, igual que el switch CxC/CxP de Informes.
          flex:"0 0 auto" (en vez de "1 1 0") para que cada pestaña quede compacta
          y agrupada a la izquierda, sin estirarse a llenar todo el ancho de la
          barra en monitores anchos (eso hacía que el ícono+texto se vieran
          "centrados" dentro de una caja enorme en vez de a la izquierda). */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "var(--bg-card)",
        borderRadius: 13, padding: 5, border: "1px solid var(--border-card)",
        overflowX: "auto", justifyContent: "flex-start" }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                flex: "0 0 auto", minWidth: 118, padding: "10px 14px", borderRadius: 9,
                background: active ? "linear-gradient(135deg,#f97316,#fb923c)" : "transparent",
                border: "none",
                color: active ? "#fff" : "var(--text-muted)",
                boxShadow: active ? "0 3px 10px rgba(249,115,22,0.32)" : "none",
                fontWeight: active ? 700 : 500, fontSize: "0.84rem",
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, transition: "all 0.18s", whiteSpace: "nowrap",
              }}
              onMouseOver={e => { if (!active) e.currentTarget.style.background = "var(--hover-bg)"; }}
              onMouseOut={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize: "0.8rem" }} />{t.label}
            </button>
          );
        })}
      </div>

      {/* Content header — título de la pestaña activa */}
      {activeTab && (
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
          <i className={`fa-solid ${activeTab.icon}`} style={{ color: "#f97316", fontSize: "0.82rem" }} />
          <span style={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.7px",
            textTransform: "uppercase", color: "var(--text-muted)" }}>
            {activeTab.label}
          </span>
        </div>
      )}

      {/* Content */}
      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
        <AdminErrorBoundary key={tab}>
          {tab === "condominios" && <CondominiosTab />}
          {tab === "usuarios"    && <UsuariosTab />}
          {tab === "contadores"  && <ContadoresTab />}
          {tab === "agua"        && <ContadoresTab perfil={PERFIL_AGUA} />}
          {tab === "seguridad"   && <ContadoresTab perfil={PERFIL_SEGURIDAD} />}
          {tab === "documentos"  && <DocumentosTab />}
          {tab === "backups"     && <BackupsTab />}
          {tab === "mantenimiento" && <MantenimientoTab />}
          {tab === "novedades"   && <NovedadesTab />}
        {tab === "demo"        && <DemoTab />}
        </AdminErrorBoundary>
      </div>
    </div>
  );
}
